import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertColor,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

import { VoucherWarningResolutionKey, voucherTemplatedFieldConfigs } from '@luxuryescapes/contract-svc-promo';

import AiToolsSection from '~/components/AiChat/AiToolsSection';
import AiWorkflowProgress from '~/components/Common/AiWorkflowProgress';
import AuditEventSummary from '~/components/Common/AuditEventSummary';
import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import StickyFormActions from '~/components/Common/Forms/StickyFormActions';
import SummaryToggleAccordion from '~/components/Common/Forms/SummaryToggleAccordion';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import PromoAdminRequiredIndicator from '~/components/Promo/PromoAdminRequiredIndicator';
import PromoAiQueryExamples from '~/components/Promo/PromoAiQueryExamples';
import PromoFeature from '~/components/Promo/PromoFeature';
import PromoResolution, { type PromoResolutionTargets } from '~/components/Promo/PromoResolution';
import PromoByToggleTester from '~/components/Promo/PromoTester/PromoByToggleTester';
import VoucherQuickTest from '~/components/Promo/PromoTester/VoucherQuickTest';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherCheck from '~/components/Vouchers/VoucherCheck';
import VoucherCloneDialog from '~/components/Vouchers/VoucherCloneDialog';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLongExpiryPromoAdminAlert from '~/components/Vouchers/VoucherLongExpiryPromoAdminAlert';
import VoucherPreviewContent, {
  getUntranslatedVoucherPreviewFields,
} from '~/components/Vouchers/VoucherPreviewContent';
import VoucherPublicPreviewCard from '~/components/Vouchers/VoucherPublicPreviewCard';
import VoucherRecentRequests from '~/components/Vouchers/VoucherRecentRequests';
import VoucherRewards, { type VoucherRewardNavigationTarget } from '~/components/Vouchers/VoucherRewards';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';
import { buildSuggestedClonedVoucherName } from '~/components/Vouchers/voucherCloneUtils';
import { serializeVoucherConditions } from '~/components/Vouchers/voucherConditionPayload';
import { VOUCHER_NAME_PATTERN_MESSAGE, validateRequiredVoucherName } from '~/components/Vouchers/voucherNameValidation';
import { validateVoucherTextReplacements } from '~/components/Vouchers/voucherTextReplacementValidation';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetLanguageOptions } from '~/hooks/apis/lab/useGetLanguageOptions';
import { useListLabAudiences } from '~/hooks/apis/lab/useListLabAudiences';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import { useGetExtendedVoucherWarnings } from '~/hooks/apis/promo/useGetExtendedVoucherWarnings';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { usePromoVoucherAiWorkflow } from '~/hooks/apis/promo/usePromoVoucherAiWorkflow';
import { useVoucherPreview } from '~/hooks/apis/promo/useVoucherPreview';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useDebounced from '~/hooks/useDebounced';
import { useSessionStorage } from '~/hooks/useSessionStorage';

import type { LabAudienceListItem, LanguageOption } from '~/services/LabService';
import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  PreviewVoucherPayload,
  PromoConditionTypeConfig,
  PromoMeta,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  VoucherUserExpiryApiPayload,
  type VoucherWarning,
  createVoucher,
  getVoucher,
  syncVoucherLabAudience,
  updateVoucher,
} from '~/services/PromoService';
import { type AiCodeMessage, type AiCodeProgressEvent, generateAiCodeName } from '~/services/PromoVoucherAiService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import ApiError from '~/types/ApiError';

import {
  getDefaultAddVoucherUsersExpiryDate,
  isoToSydneyDate,
  normalizeSydneyFormDateStringToIso,
  normalizeToISODate,
} from '~/utils/dateFormatter';
import { getDependentItemFieldsFromSelectedConditions } from '~/utils/promoRequestItemFieldDefaults';
import {
  FALLBACK_VOUCHER_REWARD_KIND_OPTIONS,
  type VoucherRewardKindOptionMeta,
  filterVoucherRewardKindOptionsForAdmin,
  voucherRewardKindOptionByKey,
} from '~/utils/voucherRewardKindMeta';
import { isVoucherUserExpiryOverTwelveMonths } from '~/utils/voucherUserExpiryLongDuration';

type RouteParams = {
  voucherId?: string;
};

type VoucherFormLocationState = { fromVoucherCreate?: { voucherName: string } } | null | undefined;

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherPresentationConfig = NonNullable<CreateVoucherPayload['voucher_presentation_config']>;
type VoucherToggleValue = string;
type VoucherToggleOption = {
  key: string;
  isActive: boolean;
  internalDescription: string;
  internalDescriptionLong: string;
  isBeingDeprecated?: boolean;
};

type VoucherApiFields = {
  voucherName?: string;
  voucherTitle?: string;
  voucherDescription?: string;
  voucherCallToAction?: string | null;
  voucherCallToActionUrl?: string | null;
  voucherPresentationConfig?: VoucherPresentationConfig | null;
  voucherBreakdownTitle?: string | null;
  voucherAmountDisplayOverride?: string | null;
  voucherWarnings?: Array<VoucherWarning>;
  currencyCode?: string;
  voucherRewards?: Array<VoucherReward>;
  voucherConditions?: Array<VoucherCondition>;
  voucherToggles?: Array<VoucherToggleValue>;
  showVoucherFromDate?: string;
  theme?: VoucherTheme;
  productType?: VoucherProductType;
  product_type?: VoucherProductType;
  voucher_name?: string;
  voucher_title?: string;
  voucher_description?: string;
  voucher_call_to_action?: string | null;
  voucher_call_to_action_url?: string | null;
  voucher_presentation_config?: VoucherPresentationConfig | null;
  voucher_breakdown_title?: string | null;
  voucher_amount_display_override?: string | null;
  currency_code?: string;
  voucher_rewards?: Array<VoucherReward>;
  voucher_conditions?: Array<VoucherCondition>;
  voucher_toggles?: Array<VoucherToggleValue>;
  show_voucher_from_date?: string;
};

type VoucherTheme = NonNullable<Voucher['theme']>;
type VoucherProductType = NonNullable<Voucher['productType']>;
type VoucherProductTypeMetaRow = PromoMeta['voucher_product_types'][number];
type VoucherThemeMetaRow = PromoMeta['voucher_themes'][number];
type VoucherRewardKind = Voucher['rewardKind'];

/** Only used when promo meta failed to load or returned an empty list. */
const FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS: Array<VoucherProductType> = [
  'flight',
  'generic',
  'hotel',
  'cruise',
  'tour',
  'carhire',
];

const VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE: VoucherWarning['code'] = 'VOUCHER_USER_EXPIRY_NOT_SET';

const voucherTemplatedFieldLongDescriptions = new Map(
  voucherTemplatedFieldConfigs.map((field) => [field.key, field.longDescription]),
);

function getVoucherTemplatedFieldLongDescription(
  key: (typeof voucherTemplatedFieldConfigs)[number]['key'],
  fallbackDescription: string,
): string {
  const description = voucherTemplatedFieldLongDescriptions.get(key);
  if (description) {
    return description;
  }

  console.warn(`Missing long description for voucher presentation field: ${key}`);
  return fallbackDescription;
}

function voucherUserExpiryWarningFieldSx(theme: Theme) {
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.main,
      borderWidth: 2,
    },
  };
}

type VoucherFormValues = {
  voucherName: string;
  voucherTitle: string;
  voucherDescription: string;
  /** Overrides the default call-to-action text shown with the voucher before redemption. */
  voucherCallToAction: string;
  /** Optional destination link for the voucher call-to-action. */
  voucherCallToActionUrl: string;
  /** Per-voucher controls for the presentation of voucher details and condition evaluation. */
  showCallToActionButtonInDetails: boolean;
  showConditionEvaluationWhenFailing: boolean;
  showConditionEvaluationWhenPassing: boolean;
  /** Preserved for existing vouchers; this admin form does not expose a control for it. */
  showFullDebugForInternalUsers?: boolean;
  /** Overrides this voucher's label in the checkout discount breakdown (default "Voucher discount"). */
  voucherBreakdownTitle: string;
  voucherAmountDisplayOverride: string;
  /** Internal support reference; stored uppercased server-side. */
  caseId: string;
  /** Internal note (why the voucher exists). */
  reason: string;
  /** svc-lab audience id for Sync to Lab only (not persisted on the voucher). */
  labAudienceId: number | null;
  currencyCode: string;
  rewardKind: VoucherRewardKind;
  voucherRewards: Array<VoucherReward>;
  voucherConditions: Array<VoucherCondition>;
  voucherToggles: Array<VoucherToggleValue>;
  showVoucherFromDate: string;
  voucherUserExpiresAt: string;
  voucherUserExpiresInDays: string;
  theme: VoucherTheme;
  productType: VoucherProductType;
};

const hasAdvancedPresentationFieldError = (errors: FieldErrors<VoucherFormValues>): boolean =>
  Boolean(
    errors.voucherCallToAction ||
    errors.voucherCallToActionUrl ||
    errors.voucherBreakdownTitle ||
    errors.voucherAmountDisplayOverride,
  );

type VoucherPreviewPayloadBase = Omit<PreviewVoucherPayload, 'previewCurrencyCode' | 'previewExpiresAt' | 'locale'>;

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_MIN_SPEND = 500;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];
const DEFAULT_VOUCHER_PREVIEW_LOCALE = 'en-AU';
const DEFAULT_VOUCHER_PREVIEW_LOCALES = ['en-AU', 'en-NZ', 'en-SG', 'en-US', 'en-GB'];
const accordionSubtitleSx = {
  display: 'none',
  '.Mui-expanded &': { display: 'block' },
};

function getVoucherPreviewLocaleOptions(localeOptions: Array<LanguageOption>): {
  common: Array<LanguageOption>;
  additional: Array<LanguageOption>;
} {
  const optionsByLocale = new Map(localeOptions.map((option) => [option.value, option]));
  if (!optionsByLocale.has(DEFAULT_VOUCHER_PREVIEW_LOCALE)) {
    optionsByLocale.set(DEFAULT_VOUCHER_PREVIEW_LOCALE, {
      value: DEFAULT_VOUCHER_PREVIEW_LOCALE,
      label: 'English (Australia)',
    });
  }

  const allOptions = Array.from(optionsByLocale.values());
  const common = DEFAULT_VOUCHER_PREVIEW_LOCALES.map((locale) => optionsByLocale.get(locale)).filter(
    (option): option is LanguageOption => Boolean(option),
  );
  const commonLocales = new Set(common.map((option) => option.value));

  return {
    common,
    additional: allOptions.filter((option) => !commonLocales.has(option.value)),
  };
}

function voucherUserExpiryToApiPayload(
  voucherUserExpiresAt: string,
  voucherUserExpiresInDays: string,
  limitVoucherUserExpiryDays: boolean,
): VoucherUserExpiryApiPayload {
  const expiresAtTrimmed = voucherUserExpiresAt.trim();
  const expiresInDaysTrimmed = limitVoucherUserExpiryDays ? voucherUserExpiresInDays.trim() : '';
  let expiresInDaysNumber: number | undefined;
  if (expiresInDaysTrimmed !== '') {
    const parsed = Number(expiresInDaysTrimmed);
    if (Number.isInteger(parsed) && parsed > 0) {
      expiresInDaysNumber = parsed;
    }
  }
  return {
    voucher_user_expires_at: expiresAtTrimmed ? normalizeToISODate(expiresAtTrimmed) : null,
    voucher_user_expires_in_days:
      expiresInDaysTrimmed === '' ? null : expiresInDaysNumber !== undefined ? expiresInDaysNumber : null,
  };
}

function getNormalizedVoucherRewards(data: VoucherFormValues, isEdit: boolean): Array<VoucherReward> {
  return data.voucherRewards.map((reward): VoucherReward => {
    const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
    const parsedMinSpendRaw =
      reward.min_spend == null || typeof reward.min_spend !== 'number' ? Number.NaN : Number(reward.min_spend);
    const parsedMinSpend = Number.isFinite(parsedMinSpendRaw) && parsedMinSpendRaw > 0 ? parsedMinSpendRaw : undefined;
    /** Only inject default min spend on create/preview — loaded vouchers already satisfy pairing when edited. */
    const minSpendForApi =
      minSpendProducts.length > 0 ? (parsedMinSpend ?? (isEdit ? undefined : DEFAULT_MIN_SPEND)) : parsedMinSpend;

    const { min_spend: _discardMinSpend, min_spend_products: _discardMinSpendProducts, ...rewardRest } = reward;

    return {
      ...rewardRest,
      currency_code: (reward.currency_code || data.currencyCode || 'AUD') as unknown as VoucherReward['currency_code'],
      reward_amount: Number(reward.reward_amount),
      min_spend_products: minSpendProducts,
      ...(minSpendForApi !== undefined ? { min_spend: minSpendForApi } : {}),
    };
  });
}

function getExistingVoucherConditionIds(voucher: Voucher | null): ReadonlySet<string> {
  if (!voucher) {
    return new Set();
  }

  const data = voucher as VoucherApiFields;
  return new Set(
    (data.voucherConditions ?? data.voucher_conditions ?? []).flatMap((condition) =>
      condition.id_voucher_condition ? [condition.id_voucher_condition] : [],
    ),
  );
}

function getShowVoucherFromDateValue(data: VoucherFormValues, includeTime: boolean): string {
  const rawShowFrom = (data.showVoucherFromDate || '').trim();
  if (!rawShowFrom) {
    return '';
  }
  if (includeTime) {
    return normalizeSydneyFormDateStringToIso(data.showVoucherFromDate);
  }
  return normalizeToISODate(rawShowFrom.length === 10 ? rawShowFrom : rawShowFrom.slice(0, 10) || rawShowFrom);
}

function buildVoucherPreviewPayloadBase(params: {
  data: VoucherFormValues;
  brand: CreateVoucherPayload['brand'];
  existingVoucherConditionIds: ReadonlySet<string>;
  isEdit: boolean;
  showVoucherFromDateIncludeTime: boolean;
  limitVoucherUserExpiryDays: boolean;
}): VoucherPreviewPayloadBase | null {
  const {
    data,
    brand,
    existingVoucherConditionIds,
    isEdit,
    showVoucherFromDateIncludeTime,
    limitVoucherUserExpiryDays,
  } = params;
  if (!isEdit && getVoucherPreviewMissingFieldMessages(data, isEdit).length > 0) {
    return null;
  }
  const showVoucherFromDateValue = getShowVoucherFromDateValue(data, showVoucherFromDateIncludeTime);
  const { voucher_user_expires_at, voucher_user_expires_in_days } = voucherUserExpiryToApiPayload(
    data.voucherUserExpiresAt,
    data.voucherUserExpiresInDays,
    limitVoucherUserExpiryDays,
  );

  return {
    voucher_name: data.voucherName.trim(),
    brand,
    voucher_title: data.voucherTitle,
    voucher_description: data.voucherDescription,
    ...(data.voucherCallToAction.trim() ? { voucher_call_to_action: data.voucherCallToAction.trim() } : {}),
    ...(data.voucherCallToActionUrl.trim() ? { voucher_call_to_action_url: data.voucherCallToActionUrl.trim() } : {}),
    voucher_presentation_config: getVoucherPresentationConfig(data),
    ...(data.voucherBreakdownTitle.trim() ? { voucher_breakdown_title: data.voucherBreakdownTitle.trim() } : {}),
    ...(data.voucherAmountDisplayOverride.trim()
      ? { voucher_amount_display_override: data.voucherAmountDisplayOverride.trim() }
      : {}),
    voucher_rewards: getNormalizedVoucherRewards(data, isEdit),
    voucher_conditions: serializeVoucherConditions(data.voucherConditions, existingVoucherConditionIds),
    voucher_toggles: data.voucherToggles as PreviewVoucherPayload['voucher_toggles'],
    ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
    voucher_user_expires_at: voucher_user_expires_at ?? undefined,
    voucher_user_expires_in_days: voucher_user_expires_in_days ?? undefined,
    theme: data.theme,
    product_type: data.productType,
    ...(data.caseId.trim() ? { case_id: data.caseId.trim().toUpperCase() } : {}),
    ...(data.reason.trim() ? { reason: data.reason.trim() } : {}),
    reward_kind: data.rewardKind,
  };
}

function getVoucherPresentationConfig(
  data: Pick<
    VoucherFormValues,
    | 'showCallToActionButtonInDetails'
    | 'showConditionEvaluationWhenFailing'
    | 'showConditionEvaluationWhenPassing'
    | 'showFullDebugForInternalUsers'
  >,
): VoucherPresentationConfig {
  return {
    showCallToActionButtonInDetails: data.showCallToActionButtonInDetails,
    showConditionEvaluationWhenFailing: data.showConditionEvaluationWhenFailing,
    showConditionEvaluationWhenPassing: data.showConditionEvaluationWhenPassing,
    ...(data.showFullDebugForInternalUsers === undefined
      ? {}
      : { showFullDebugForInternalUsers: data.showFullDebugForInternalUsers }),
  };
}

function hasAdvancedVoucherPresentationValues(
  values: Pick<
    VoucherFormValues,
    | 'theme'
    | 'voucherCallToAction'
    | 'voucherCallToActionUrl'
    | 'voucherBreakdownTitle'
    | 'voucherAmountDisplayOverride'
    | 'showCallToActionButtonInDetails'
    | 'showConditionEvaluationWhenFailing'
    | 'showConditionEvaluationWhenPassing'
  >,
): boolean {
  return (
    values.theme !== 'default' ||
    values.voucherCallToAction.trim() !== '' ||
    values.voucherCallToActionUrl.trim() !== '' ||
    values.voucherBreakdownTitle.trim() !== '' ||
    values.voucherAmountDisplayOverride.trim() !== '' ||
    !values.showCallToActionButtonInDetails ||
    values.showConditionEvaluationWhenFailing ||
    values.showConditionEvaluationWhenPassing
  );
}

function getVoucherPreviewMissingFieldMessages(data: VoucherFormValues, isEdit: boolean): Array<string> {
  if (isEdit) {
    return [];
  }

  const voucherNameValidation = validateVoucherName(data.voucherName);
  return voucherNameValidation === true ? [] : [`Voucher name: ${voucherNameValidation}`];
}

function validateVoucherName(value: string): true | string {
  return validateRequiredVoucherName(value);
}

const buildDefaultFormValues = (): VoucherFormValues => ({
  voucherName: '',
  voucherTitle: '',
  voucherDescription: '',
  voucherCallToAction: '',
  voucherCallToActionUrl: '',
  showCallToActionButtonInDetails: true,
  showConditionEvaluationWhenFailing: false,
  showConditionEvaluationWhenPassing: false,
  voucherBreakdownTitle: '',
  voucherAmountDisplayOverride: '',
  caseId: '',
  reason: '',
  labAudienceId: null,
  currencyCode: 'AUD',
  rewardKind: 'discount',
  voucherRewards: [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: DEFAULT_MIN_SPEND,
      min_spend_products: [...DEFAULT_REWARD_PRODUCTS] as NonNullable<VoucherReward['min_spend_products']>,
    },
  ],
  voucherConditions: [],
  voucherToggles: [],
  showVoucherFromDate: '',
  voucherUserExpiresAt: '',
  voucherUserExpiresInDays: '',
  theme: 'default',
  productType: 'generic',
});

/**
 * True when the API value is only a calendar start date (no specific time of day):
 * a bare YYYY-MM-DD, Sydney start-of-day, or the legacy start-of-day-UTC serialization.
 */
function isStartDateOnlyInApi(s: string): boolean {
  const t = s.trim();
  if (!t) {
    return true;
  }
  if (!t.includes('T')) {
    return true;
  }
  if (/T00:00:00(\.0+)?Z$/.test(t) || /T00:00:00(\.0+)?\+00:00$/.test(t)) {
    return true;
  }
  const d = dayjs(t);
  return d.isValid() && d.tz(SYDNEY_TIMEZONE).format('HH:mm:ss.SSS') === '00:00:00.000';
}

/** Maps API "show from" to form: date field by default; date+time if a specific instant is stored. */
function getShowVoucherFromFieldsFromApi(raw: string | undefined | null): { value: string; includeTime: boolean } {
  if (!raw?.toString().trim()) {
    return { value: '', includeTime: false };
  }
  const s = raw.toString().trim();
  if (isStartDateOnlyInApi(s)) {
    return {
      // Legacy UTC-midnight values keep their stored calendar day; Sydney-midnight values map back via tz.
      value: /T00:00:00(\.0+)?(Z|\+00:00)$/.test(s) ? s.slice(0, 10) : isoToSydneyDate(s),
      includeTime: false,
    };
  }
  const d = dayjs(s);
  return {
    value: d.isValid() ? d.tz(SYDNEY_TIMEZONE).format('YYYY-MM-DDTHH:mm') : '',
    includeTime: true,
  };
}

const parseVoucherApiResponse = (voucher: Voucher): VoucherFormValues => {
  const data = voucher as VoucherApiFields;
  const raw = voucher as unknown as Record<string, unknown>;
  const rawCurrencyCode = data.currencyCode || data.currency_code;
  const rawExpiresInDays = raw.voucherUserExpiresInDays ?? raw.voucher_user_expires_in_days;
  const voucherWithCase = voucher as Voucher & { case_id?: string };
  const caseFromApi = voucherWithCase.caseId ?? voucherWithCase.case_id;
  const voucherRewardKindFields = voucher as Voucher & { reward_kind?: string };
  const rewardKindRaw = voucherRewardKindFields.rewardKind ?? voucherRewardKindFields.reward_kind;
  const rewardKindTrimmed =
    rewardKindRaw != null && String(rewardKindRaw).trim() !== '' ? String(rewardKindRaw).trim() : '';
  const rewardKind: VoucherRewardKind = (
    rewardKindTrimmed !== '' ? rewardKindTrimmed : 'discount'
  ) as VoucherRewardKind;
  const voucherPresentationConfig = data.voucherPresentationConfig ?? data.voucher_presentation_config;
  return {
    voucherName: data.voucherName || data.voucher_name || '',
    voucherTitle: data.voucherTitle || data.voucher_title || '',
    voucherDescription: data.voucherDescription || data.voucher_description || '',
    voucherCallToAction: data.voucherCallToAction || data.voucher_call_to_action || '',
    voucherCallToActionUrl: data.voucherCallToActionUrl || data.voucher_call_to_action_url || '',
    showCallToActionButtonInDetails: voucherPresentationConfig?.showCallToActionButtonInDetails ?? true,
    showConditionEvaluationWhenFailing: voucherPresentationConfig?.showConditionEvaluationWhenFailing ?? false,
    showConditionEvaluationWhenPassing: voucherPresentationConfig?.showConditionEvaluationWhenPassing ?? false,
    showFullDebugForInternalUsers: voucherPresentationConfig?.showFullDebugForInternalUsers,
    voucherBreakdownTitle: data.voucherBreakdownTitle || data.voucher_breakdown_title || '',
    voucherAmountDisplayOverride: String(
      data.voucherAmountDisplayOverride ?? data.voucher_amount_display_override ?? '',
    ),
    caseId: caseFromApi != null && String(caseFromApi).trim() !== '' ? String(caseFromApi) : '',
    reason: voucher.reason != null && String(voucher.reason).trim() !== '' ? String(voucher.reason) : '',
    labAudienceId: null,
    currencyCode: rawCurrencyCode && currencyCodes.includes(rawCurrencyCode) ? rawCurrencyCode : 'AUD',
    rewardKind,
    voucherRewards: (data.voucherRewards ?? data.voucher_rewards ?? []).map((reward) => ({
      ...reward,
      id_voucher_reward: reward.id_voucher_reward || uuid(),
    })),
    voucherConditions: (data.voucherConditions ?? data.voucher_conditions ?? []).map((condition) => ({
      ...condition,
      id_voucher_condition: condition.id_voucher_condition || uuid(),
    })),
    voucherToggles: data.voucherToggles ?? data.voucher_toggles ?? [],
    showVoucherFromDate: getShowVoucherFromFieldsFromApi(data.showVoucherFromDate || data.show_voucher_from_date).value,
    voucherUserExpiresAt: isoToSydneyDate(String(raw.voucherUserExpiresAt ?? raw.voucher_user_expires_at ?? '')),
    voucherUserExpiresInDays: rawExpiresInDays != null && rawExpiresInDays !== '' ? String(rawExpiresInDays) : '',
    theme: data.theme || 'default',
    productType: data.productType || data.product_type || 'generic',
  };
};

const getVoucherWarningSeverity = (level?: string): AlertColor => {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
};

/** `request()` maps JSON error bodies onto `ApiError` (`message`, `status`, `errors`). */
function formatVoucherSaveFailure(error: unknown): string {
  if (error instanceof ApiError) {
    const parts: Array<string> = [error.message];
    if (error.status != null) {
      parts.push(`HTTP ${error.status}`);
    }
    const { errors } = error;
    let bodyDetails: string | null = null;
    if (errors !== undefined && errors !== null) {
      if (typeof errors === 'string') {
        const trimmed = errors.trim();
        bodyDetails = trimmed.length > 0 ? trimmed : null;
      } else if (Array.isArray(errors)) {
        bodyDetails = errors.length > 0 ? JSON.stringify(errors, null, 2) : null;
      } else {
        const serialized = JSON.stringify(errors, null, 2);
        bodyDetails = serialized !== '{}' && serialized !== 'null' ? serialized : null;
      }
    }
    if (bodyDetails) {
      parts.push(bodyDetails);
    }
    return parts.join(' — ');
  }
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const isEdit = Boolean(voucherId);
  const { block, push, replace } = useAppNavigation();
  const location = useLocation();
  const fromCreate = (location.state as VoucherFormLocationState)?.fromVoucherCreate;
  const focusedVoucherId = useMemo(
    () => new URLSearchParams(location.search).get('focusVoucherRequests'),
    [location.search],
  );
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, formState, watch, setValue, getValues, trigger } = useForm<VoucherFormValues>({
    defaultValues: buildDefaultFormValues(),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const { isDirty, isSubmitting, dirtyFields, errors: formErrors } = formState;
  const { generate: aiGenerate } = usePromoVoucherAiWorkflow();
  const aiWorkflow = new URLSearchParams(location.search).get('aiWorkflow');
  const aiInitialName = new URLSearchParams(location.search).get('aiVoucherName') ?? '';
  const [aiRequest, setAiRequest] = useState<string>(
    typeof location.state?.aiRequest === 'string' ? location.state.aiRequest : '',
  );
  const [hasAiRequestBeenFocused, setHasAiRequestBeenFocused] = useState(false);
  const [aiConversation, setAiConversation] = useState<Array<AiCodeMessage>>([]);
  const [aiPreviousProposal, setAiPreviousProposal] = useState<{
    codeName: string;
    brand: CreateVoucherPayload['brand'];
    operation: 'create' | 'update';
    payload: CreateVoucherPayload | UpdateVoucherPayload;
  } | null>(null);
  const [aiProgress, setAiProgress] = useState<Array<AiCodeProgressEvent>>([]);
  const [aiFields, setAiFields] = useState<Array<keyof VoucherFormValues>>([]);
  const manualEditsRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    const subscription = watch((values, event) => {
      if (!event.name || !event.type) {
        return;
      }
      const field = event.name.split('.')[0];
      manualEditsRef.current[field] = (values as Record<string, unknown>)[field];
      setAiFields((current) => current.filter((name) => name !== field));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /** When true, the next in-app navigation is allowed without the unsaved-changes confirm (e.g. after delete). */
  const bypassUnsavedNavigationBlockRef = useRef(false);
  /** Values loaded from API (edit mode) — used to warn before clearing case ID / reason. */
  const initialInternalFieldsRef = useRef<{ caseId: string; reason: string }>({ caseId: '', reason: '' });
  const watchedVoucherUserExpiresAt = watch('voucherUserExpiresAt');
  const watchedVoucherUserExpiresInDays = watch('voucherUserExpiresInDays');
  const watchedVoucherDescription = watch('voucherDescription');
  const watchedLabAudienceId = watch('labAudienceId');

  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  /** When true, the show-from field includes a time; when false, start date only (the usual case). */
  const [showVoucherFromDateIncludeTime, setShowVoucherFromDateIncludeTime] = useState(false);
  const [voucherUsers, setVoucherUsers] = useState<Array<string>>([]);
  /** User ID filter shared between voucher users grid and Recent Requests (voucher edit page only). */
  const [promoRequestsUserFilter, setPromoRequestsUserFilter] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [limitVoucherUserExpiryDays, setLimitVoucherUserExpiryDays] = useState(false);
  /** Keep validation in sync with the checkbox before React paints the next state. */
  const limitVoucherUserExpiryDaysRef = useRef(false);
  const [descriptionAccordionExpanded, setDescriptionAccordionExpanded] = useState(false);
  const [statusContextExpanded, setStatusContextExpanded] = useState(true);
  const [showExtendedWarnings, setShowExtendedWarnings] = useSessionStorage(
    'voucher-form-show-extended-warnings',
    false,
  );
  const [voucherWarningsExpanded, setVoucherWarningsExpanded] = useState(false);
  const [advancedPresentationAccordionExpanded, setAdvancedPresentationAccordionExpanded] = useState(false);
  const hasAdvancedPresentationError = hasAdvancedPresentationFieldError(formErrors);
  const [showAllPreviewLocales, setShowAllPreviewLocales] = useState(false);
  const lastAutoPreviewKeyRef = useRef('');
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [presentationExpanded, setPresentationExpanded] = useState(false);
  const [rewardsExpanded, setRewardsExpanded] = useState(true);
  const [resolutionField, setResolutionField] = useState<
    'productType' | 'expiry' | 'conditions' | VoucherRewardNavigationTarget['field']
  >();
  const productTypeRef = useRef<HTMLDivElement>(null);
  const rewardsRef = useRef<HTMLDivElement>(null);
  const expiryRef = useRef<HTMLInputElement>(null);
  const conditionsRef = useRef<HTMLDivElement>(null);
  const pendingRewardNavigation = useRef<VoucherRewardNavigationTarget | undefined>(undefined);
  const pendingResolutionNavigation = useRef(false);
  const [rewardNavigationTarget, setRewardNavigationTarget] = useState<VoucherRewardNavigationTarget>();
  const navigateToResolutionField = useCallback(
    (field = resolutionField) => {
      if (!pendingResolutionNavigation.current) {
        return;
      }
      if (field === 'productType') {
        if (!presentationExpanded) {
          return;
        }
        productTypeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        productTypeRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus({ preventScroll: true });
      } else if (field === 'expiry') {
        if (!statusContextExpanded) return;
        expiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        expiryRef.current?.focus({ preventScroll: true });
      } else if (field === 'conditions') {
        if (!statusContextExpanded || !rewardsExpanded) return;
        conditionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        conditionsRef.current?.focus({ preventScroll: true });
      } else if (field) {
        if (!statusContextExpanded || !rewardsExpanded) {
          return;
        }
        rewardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (pendingRewardNavigation.current) {
          setRewardNavigationTarget({ ...pendingRewardNavigation.current });
        }
      }
      pendingResolutionNavigation.current = false;
    },
    [resolutionField, presentationExpanded, statusContextExpanded, rewardsExpanded],
  );
  function getWarningResolutionTargets(warning: VoucherWarning): PromoResolutionTargets {
    const toggleToAdd = warning.resolution?.toggleToAdd;
    const hasRequiredToggle = Boolean(toggleToAdd && voucherToggles.includes(toggleToAdd));
    const goToReward = (field: VoucherRewardNavigationTarget['field']) => {
      pendingResolutionNavigation.current = true;
      pendingRewardNavigation.current = { field, rewardId: warning.voucherRewardId };
      setResolutionField(field);
      setStatusContextExpanded(true);
      setRewardsExpanded(true);
      if (statusContextExpanded && rewardsExpanded) {
        navigateToResolutionField(field);
      }
    };
    const goToSection = (field: 'expiry' | 'conditions') => {
      pendingResolutionNavigation.current = true;
      setResolutionField(field);
      setStatusContextExpanded(true);
      if (field === 'conditions') setRewardsExpanded(true);
      navigateToResolutionField(field);
    };
    return {
      [VoucherWarningResolutionKey.ADD_REWARD]: {
        label: 'Add reward',
        disabled: isSubmitting || isPromoMetaLoading,
        onResolve: () => goToReward('add'),
      },
      [VoucherWarningResolutionKey.SET_EXPIRY_DATE]: {
        label: 'Set expiry',
        onResolve: () => goToSection('expiry'),
      },
      [VoucherWarningResolutionKey.CONDITION_SET_REWARD_IDS]: {
        label: 'Edit conditions',
        onResolve: () => goToSection('conditions'),
      },
      ...(toggleToAdd && {
        [VoucherWarningResolutionKey.ADD_REQUIRED_TOGGLE]: {
          label: hasRequiredToggle ? 'Toggle added — save voucher' : 'Add toggle',
          disabled: isSubmitting || hasRequiredToggle,
          onResolve: () => {
            const currentToggles = getValues('voucherToggles');
            if (currentToggles.includes(toggleToAdd)) return;

            setValue('voucherToggles', [...currentToggles, toggleToAdd], {
              shouldDirty: true,
              shouldValidate: true,
            });
            enqueueSnackbar(`Added ${toggleToAdd}. Save the voucher to apply this change.`, { variant: 'info' });
          },
        },
      }),
      [VoucherWarningResolutionKey.SET_PRODUCT_TYPE]: {
        label: 'Go To',
        onResolve: () => {
          pendingResolutionNavigation.current = true;
          setResolutionField('productType');
          setPresentationExpanded(true);
          if (presentationExpanded) {
            navigateToResolutionField('productType');
          }
        },
      },
      [VoucherWarningResolutionKey.REWARD_SET_CURRENCY]: {
        label: 'Go To',
        onResolve: () => goToReward('currency_code'),
      },
      [VoucherWarningResolutionKey.REWARD_SET_AMOUNT]: { label: 'Go To', onResolve: () => goToReward('reward_amount') },
      [VoucherWarningResolutionKey.REWARD_SET_MIN_SPEND]: { label: 'Go To', onResolve: () => goToReward('min_spend') },
    };
  }
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [bulkExpiryExpanded, setBulkExpiryExpanded] = useState(false);
  const bulkExpiryFormRef = useRef<HTMLDivElement>(null);
  const scrollToBulkExpiryForm = useCallback(() => {
    bulkExpiryFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const [labAudienceSearch, setLabAudienceSearch] = useState('');
  const debouncedLabAudienceSearch = useDebounced(labAudienceSearch, 300);
  const hasUnsavedRewardChanges = Boolean(dirtyFields.voucherRewards);
  const hasUnsavedConditionChanges = Boolean(dirtyFields.voucherConditions);
  const currentBrand = useCurrentBrand();
  let brand = toPromoBrand(currentBrand);
  if (!brand) {
    console.error(`could not load promo brand ${currentBrand}. default to luxuryescapes`);
    brand = 'luxuryescapes';
  }
  useEffect(() => {
    setAiConversation([]);
    setAiPreviousProposal(null);
    setAiProgress([]);
    setAiRequest('');
  }, [brand]);
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const { data: languageOptions = [] } = useGetLanguageOptions();

  const {
    data: voucherResponse,
    isLoading: isVoucherLoading,
    isFetching: isVoucherFetching,
  } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);
  const extendedWarningsQuery = useGetExtendedVoucherWarnings({
    voucherIds: voucher ? [voucher.voucherId] : [],
    brand: voucher?.brand ?? brand,
    enabled: Boolean(voucher) && showExtendedWarnings,
  });
  const extendedWarningResult =
    showExtendedWarnings && voucher ? extendedWarningsQuery.data?.get(voucher.voucherId) : undefined;
  const lateVoucherUserExpiryGroups =
    extendedWarningResult?.extraChecks.flatMap((check) =>
      check.check === 'check-voucher-user-expiry-after-voucher-expiry' ? check.expiryGroups : [],
    ) ?? [];
  const existingVoucherConditionIds = useMemo(() => getExistingVoucherConditionIds(voucher), [voucher]);
  const voucherDescriptionSummaryPreview = useMemo(() => {
    const text = watchedVoucherDescription?.trim() ?? '';
    if (!text) {
      return 'Optional — supports markdown';
    }
    const singleLine = text.replace(/\s+/g, ' ').trim();
    const maxLength = 120;
    return singleLine.length > maxLength ? `${singleLine.slice(0, maxLength)}…` : singleLine;
  }, [watchedVoucherDescription]);

  useEffect(() => {
    if (voucher) {
      const v = voucher as Voucher & { case_id?: string };
      initialInternalFieldsRef.current = {
        caseId: String(v.caseId ?? v.case_id ?? '').trim(),
        reason: String(v.reason ?? '').trim(),
      };
    } else if (!isEdit) {
      initialInternalFieldsRef.current = { caseId: '', reason: '' };
    }
  }, [voucher, isEdit]);

  const clonedFromId = useMemo(() => {
    if (!voucher) {
      return '';
    }
    const raw = (voucher as Voucher & { clonedFromId?: string | null }).clonedFromId;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [voucher]);

  const { data: clonedFromVouchersMap } = useGetVouchersByIds({
    brand: brand,
    voucherIds: clonedFromId ? [clonedFromId] : [],
    enabled: Boolean(clonedFromId),
  });
  const clonedFromVoucherName = clonedFromId
    ? clonedFromVouchersMap?.get(clonedFromId)?.voucherName?.trim()
    : undefined;

  const voucherAuditFields = useMemo(() => {
    if (!voucher) {
      return {
        createdByUserId: '',
        modifiedByUserId: '',
        createdAt: '',
        updatedAt: '',
      };
    }
    const v = voucher as Voucher & {
      created_by?: string;
      updated_by?: string;
      modified_by?: string;
      created_at?: string;
      updated_at?: string;
    };
    return {
      createdByUserId: (v.createdBy ?? v.created_by ?? '').trim(),
      modifiedByUserId: (v.updatedBy ?? v.updated_by ?? v.modified_by ?? '').trim(),
      createdAt: v.createdAt ?? v.created_at ?? '',
      updatedAt: v.updatedAt ?? v.updated_at ?? '',
    };
  }, [voucher]);

  const voucherAuditUserIds = useMemo(
    () => [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId].filter(Boolean),
    [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId],
  );

  const { data: voucherAuditUsersById = new Map<string, App.UserSummary>() } = useGetUsersSummaryByIds({
    userIds: voucherAuditUserIds,
    brand,
    enabled: Boolean(isEdit && voucher && voucherAuditUserIds.length > 0),
  });

  const voucherWarnings = useMemo(() => {
    if (!voucher) {
      return [];
    }
    const voucherData = voucher as VoucherApiFields;
    return extendedWarningResult?.voucherWarnings ?? voucherData.voucherWarnings ?? [];
  }, [voucher, extendedWarningResult]);

  const highlightVoucherUserExpiryFields = useMemo(
    () => voucherWarnings.some((w) => w.code === VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE),
    [voucherWarnings],
  );

  const shouldShowStatusContext = Boolean(fromCreate || voucherWarnings.length > 0);

  useEffect(() => {
    if (shouldShowStatusContext) {
      setStatusContextExpanded(true);
    }
  }, [shouldShowStatusContext]);

  // Populate form from API data — guarded to run only once per voucherId
  useEffect(() => {
    if (!isEdit) {
      const defaults = buildDefaultFormValues();
      if (aiInitialName) {
        defaults.voucherName = aiInitialName;
      }
      reset(defaults);
      setAiConversation([]);
      setAiPreviousProposal(null);
      setAiRequest('');
      setAiProgress([]);
      manualEditsRef.current = {};
      setAiFields([]);
      setAdvancedPresentationAccordionExpanded(false);
      setVoucherUsers([]);
      setPromoRequestsUserFilter('');
      setExpiredAt('');
      limitVoucherUserExpiryDaysRef.current = false;
      setLimitVoucherUserExpiryDays(false);
      setShowVoucherFromDateIncludeTime(false);
      setDescriptionEditorKey((prev) => prev + 1);
      return;
    }
    if (voucher) {
      const data = voucher as VoucherApiFields;
      const raw = data.showVoucherFromDate || data.show_voucher_from_date;
      const { includeTime } = getShowVoucherFromFieldsFromApi(raw);
      const values = parseVoucherApiResponse(voucher);
      reset(values);
      setAiConversation([]);
      setAiPreviousProposal(null);
      setAiRequest('');
      setAiProgress([]);
      manualEditsRef.current = {};
      setAiFields([]);
      setAdvancedPresentationAccordionExpanded(hasAdvancedVoucherPresentationValues(values));
      setVoucherUsers([]);
      setPromoRequestsUserFilter('');
      setExpiredAt('');
      const hasExpiryDays = values.voucherUserExpiresInDays.trim() !== '';
      limitVoucherUserExpiryDaysRef.current = hasExpiryDays;
      setLimitVoucherUserExpiryDays(hasExpiryDays);
      setShowVoucherFromDateIncludeTime(includeTime);
      setDescriptionEditorKey((prev) => prev + 1);
    }
  }, [isEdit, voucher, voucherId, reset, aiInitialName]);

  const voucherNameValue = watch('voucherName');
  const aiDisabledReasonId = useId();
  const aiDisabledReason = isSubmitting
    ? 'Wait until saving finishes before preparing another proposal.'
    : aiGenerate.isPending
      ? 'Wait while the AI prepares the proposal.'
      : isEdit && isVoucherLoading
        ? 'Wait until the voucher finishes loading.'
        : isEdit && !voucherNameValue.trim()
          ? 'Enter the voucher name to update.'
          : !aiRequest.trim()
            ? 'Describe what the AI should set to prepare a proposal.'
            : '';
  const watchedVoucherTitle = watch('voucherTitle');
  const watchedVoucherAmountDisplayOverride = watch('voucherAmountDisplayOverride');
  const watchedProductType = watch('productType');
  const watchedShowVoucherFromDate = watch('showVoucherFromDate');
  const voucherToggles = watch('voucherToggles');
  const voucherRewards = watch('voucherRewards');
  const voucherConditions = watch('voucherConditions');
  const currencyCode = watch('currencyCode');
  const watchedRewardKind = watch('rewardKind');
  const watchedTheme = watch('theme');

  const voucherToggleOptions: Array<VoucherToggleOption> = promoMeta?.voucher_toggles ?? [];

  const voucherPreviewCurrencyOptions = useMemo(() => {
    const fallbackCurrency = currencyCode || 'AUD';
    return voucherRewards.map((reward) => reward.currency_code || fallbackCurrency).filter(Boolean);
  }, [currencyCode, voucherRewards]);

  const buildPreviewPayload = useCallback(
    () =>
      buildVoucherPreviewPayloadBase({
        data: getValues(),
        brand,
        existingVoucherConditionIds,
        isEdit,
        showVoucherFromDateIncludeTime,
        limitVoucherUserExpiryDays,
      }),
    [brand, existingVoucherConditionIds, getValues, isEdit, showVoucherFromDateIncludeTime, limitVoucherUserExpiryDays],
  );

  const getPreviewMissingFieldMessages = useCallback(
    () => getVoucherPreviewMissingFieldMessages(getValues(), isEdit),
    [getValues, isEdit],
  );

  const {
    handlePreview: handleRefreshPreview,
    isPreviewError,
    isPreviewPending,
    previewCurrencyCode,
    previewLocale,
    previewErrorDetailBlock,
    previewErrorHeadline,
    previewLocalError,
    previewTextReplacements,
    publicPreviewVoucher,
    resolvedCurrencyOptions: resolvedVoucherPreviewCurrencyOptions,
    setPreviewCurrencyCode,
    setPreviewLocale,
  } = useVoucherPreview({
    buildPayload: buildPreviewPayload,
    currencyOptions: voucherPreviewCurrencyOptions,
    defaultCurrencyCode: voucherPreviewCurrencyOptions[0] || currencyCode || 'AUD',
    defaultLocale: DEFAULT_VOUCHER_PREVIEW_LOCALE,
    getMissingFieldMessages: getPreviewMissingFieldMessages,
  });

  const voucherPreviewLocaleOptions = useMemo(() => getVoucherPreviewLocaleOptions(languageOptions), [languageOptions]);
  const selectedPreviewLocaleLabel = useMemo(
    () =>
      [...voucherPreviewLocaleOptions.common, ...voucherPreviewLocaleOptions.additional].find(
        (option) => option.value === previewLocale,
      )?.label ?? previewLocale,
    [previewLocale, voucherPreviewLocaleOptions],
  );
  const untranslatedPreviewFields = useMemo(
    () =>
      previewLocale === DEFAULT_VOUCHER_PREVIEW_LOCALE
        ? []
        : getUntranslatedVoucherPreviewFields(
            {
              voucherAmountDisplay: watchedVoucherAmountDisplayOverride,
              voucherDescription: watchedVoucherDescription,
              voucherTitle: watchedVoucherTitle,
            },
            publicPreviewVoucher,
          ),
    [
      previewLocale,
      publicPreviewVoucher,
      watchedVoucherAmountDisplayOverride,
      watchedVoucherDescription,
      watchedVoucherTitle,
    ],
  );

  const autoPreviewRefreshKey = useMemo(
    () =>
      JSON.stringify({
        voucherId: voucherId || 'create',
        currencyCode: previewCurrencyCode,
        locale: previewLocale,
        title: watchedVoucherTitle,
        description: watchedVoucherDescription,
        amountDisplayOverride: watchedVoucherAmountDisplayOverride,
        rewardKind: watchedRewardKind,
        rewards: voucherRewards.map((reward) => ({
          currencyCode: reward.currency_code,
          minSpend: reward.min_spend,
          minSpendProducts: reward.min_spend_products,
          products: reward.products,
          rewardAmount: reward.reward_amount,
        })),
        showVoucherFromDate: watchedShowVoucherFromDate,
        showVoucherFromDateIncludeTime,
        voucherUserExpiresAt: watchedVoucherUserExpiresAt,
        voucherUserExpiresInDays: watchedVoucherUserExpiresInDays,
        limitVoucherUserExpiryDays,
      }),
    [
      previewCurrencyCode,
      previewLocale,
      showVoucherFromDateIncludeTime,
      voucherId,
      voucherRewards,
      limitVoucherUserExpiryDays,
      watchedRewardKind,
      watchedShowVoucherFromDate,
      watchedVoucherAmountDisplayOverride,
      watchedVoucherDescription,
      watchedVoucherTitle,
      watchedVoucherUserExpiresAt,
      watchedVoucherUserExpiresInDays,
    ],
  );

  const defaultVisibleRequestItemFields = useMemo(
    () =>
      getDependentItemFieldsFromSelectedConditions(
        voucherConditions,
        (promoMeta?.voucher_condition_types as Array<PromoConditionTypeConfig> | undefined) ?? [],
      ),
    [promoMeta?.voucher_condition_types, voucherConditions],
  );
  const voucherThemeMenuRows: Array<VoucherThemeMetaRow> = useMemo(() => {
    if (isPromoMetaLoading) {
      return [];
    }
    const fromMeta = promoMeta?.voucher_themes;
    if (fromMeta && fromMeta.length > 0) {
      const keys = new Set<VoucherTheme>(fromMeta.map((row) => row.key));
      if (!keys.has(watchedTheme) && isEdit) {
        return [
          ...fromMeta,
          {
            key: watchedTheme,
            name: `[UNKNOWN] ${watchedTheme}`,
            description: 'This theme is not in current voucher metadata and will be preserved unless you change it.',
          },
        ];
      }
      return fromMeta;
    }
    if (isEdit && watchedTheme) {
      return [
        {
          key: watchedTheme,
          name: `[UNKNOWN] ${watchedTheme}`,
          description:
            'Theme options are not available from promo meta. Current value is shown until a list is available.',
        },
      ];
    }
    return [];
  }, [promoMeta, isPromoMetaLoading, watchedTheme, isEdit]);

  const voucherProductTypeMenuRows: Array<VoucherProductTypeMetaRow> = useMemo(() => {
    const fromMeta = promoMeta?.voucher_product_types;
    if (fromMeta && fromMeta.length > 0) {
      const keys = new Set<VoucherProductType>(fromMeta.map((row) => row.key as VoucherProductType));
      if (!keys.has(watchedProductType) && isEdit) {
        return [
          ...fromMeta,
          {
            key: watchedProductType,
            name: `[UNKNOWN] ${watchedProductType}`,
            description:
              'This product type is not in current voucher metadata and will be preserved unless you change it.',
          },
        ];
      }
      return fromMeta;
    }
    if (isPromoMetaLoading) {
      return [];
    }
    return FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS.map((key) => ({
      key,
      name: key,
      description: '',
    }));
  }, [promoMeta, isPromoMetaLoading, watchedProductType, isEdit]);

  const voucherRewardKindMenuRows: Array<VoucherRewardKindOptionMeta> = useMemo(() => {
    const fullByKey = voucherRewardKindOptionByKey(promoMeta);
    const fromMeta = filterVoucherRewardKindOptionsForAdmin(promoMeta);
    const keys = new Set(fromMeta.map((row) => row.key));
    if (watchedRewardKind && !keys.has(watchedRewardKind) && isEdit) {
      const fromCatalog = fullByKey.get(watchedRewardKind);
      return [
        ...fromMeta,
        fromCatalog ?? {
          key: watchedRewardKind,
          name: `[UNKNOWN] ${watchedRewardKind}`,
          description: 'This reward kind is not in current promo metadata and will be preserved unless you change it.',
          showInAdmin: true,
        },
      ];
    }
    if (fromMeta.length > 0) {
      return fromMeta;
    }
    if (isPromoMetaLoading) {
      return [];
    }
    return FALLBACK_VOUCHER_REWARD_KIND_OPTIONS;
  }, [promoMeta, isPromoMetaLoading, watchedRewardKind, isEdit]);

  const selectedUnknownVoucherToggleOptions = useMemo<Array<VoucherToggleOption>>(() => {
    const knownToggleKeys = new Set(voucherToggleOptions.map((option) => option.key));
    return voucherToggles
      .filter((key) => !knownToggleKeys.has(key))
      .map((key) => ({
        key,
        isActive: false,
        internalDescription: `[UNKNOWN] ${key}`,
        internalDescriptionLong:
          'This toggle key is not in current voucher metadata and will be preserved unless explicitly removed.',
      }));
  }, [voucherToggles, voucherToggleOptions]);

  const voucherToggleOptionsWithUnknown = useMemo<Array<VoucherToggleOption>>(() => {
    const allOptions = [...voucherToggleOptions, ...selectedUnknownVoucherToggleOptions];
    return allOptions.sort((a, b) => {
      if (a.isBeingDeprecated !== b.isBeingDeprecated) {
        return Number(a.isBeingDeprecated ?? false) - Number(b.isBeingDeprecated ?? false);
      }
      return a.internalDescription.localeCompare(b.internalDescription);
    });
  }, [voucherToggleOptions, selectedUnknownVoucherToggleOptions]);

  useEffect(() => {
    void trigger('voucherUserExpiresAt');
  }, [watchedShowVoucherFromDate, trigger]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const unblock = block(() => {
      if (bypassUnsavedNavigationBlockRef.current) {
        bypassUnsavedNavigationBlockRef.current = false;
        return undefined;
      }
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [isDirty, block]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const syncLabAudienceMutation = useMutation({
    mutationFn: async ({ id, labAudienceId }: { id: string; labAudienceId: number }) =>
      syncVoucherLabAudience(id, labAudienceId),
    onSuccess: (response) => {
      enqueueSnackbar(`Synced ${response.result.membersSynced} members to Lab`, { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(formatVoucherSaveFailure(error), { variant: 'error' });
    },
  });

  const labAudienceListQuery = useMemo(
    () => ({
      page: 1,
      ...(debouncedLabAudienceSearch.trim() ? { search: debouncedLabAudienceSearch.trim() } : {}),
    }),
    [debouncedLabAudienceSearch],
  );

  const { data: labAudiencesResponse, isFetching: isLabAudiencesFetching } = useListLabAudiences({
    query: labAudienceListQuery,
  });

  const labAudienceOptions = useMemo((): Array<LabAudienceListItem> => {
    const audiences = labAudiencesResponse?.data?.audiences ?? [];
    if (watchedLabAudienceId == null) {
      return audiences;
    }
    if (audiences.some((audience) => audience.id === watchedLabAudienceId)) {
      return audiences;
    }
    return [{ id: watchedLabAudienceId, name: `Audience #${watchedLabAudienceId} (unavailable)` }, ...audiences];
  }, [labAudiencesResponse?.data?.audiences, watchedLabAudienceId]);

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const deleteVoucherMutation = useDeleteVoucher();

  useEffect(() => {
    if (isEdit && (isVoucherLoading || !voucher)) {
      return undefined;
    }
    if (!previewCurrencyCode) {
      return undefined;
    }
    if (lastAutoPreviewKeyRef.current === autoPreviewRefreshKey) {
      return undefined;
    }
    // Keep preview updates responsive while allowing the editor to settle between keystrokes.
    const timeoutId = window.setTimeout(() => {
      lastAutoPreviewKeyRef.current = autoPreviewRefreshKey;
      void handleRefreshPreview();
    }, 450);
    return () => window.clearTimeout(timeoutId);
  }, [autoPreviewRefreshKey, handleRefreshPreview, isEdit, isVoucherLoading, previewCurrencyCode, voucher]);

  const onSubmit = async (data: VoucherFormValues) => {
    try {
      if (isEdit) {
        const initial = initialInternalFieldsRef.current;
        const clearingCaseId = initial.caseId !== '' && data.caseId.trim() === '';
        const clearingReason = initial.reason !== '' && data.reason.trim() === '';
        if (clearingCaseId || clearingReason) {
          const clearedLabels: Array<string> = [];
          if (clearingCaseId) {
            clearedLabels.push('case ID');
          }
          if (clearingReason) {
            clearedLabels.push('reason');
          }
          const proceed = window.confirm(
            `You are about to clear the voucher ${clearedLabels.join(' and ')}. Continue?`,
          );
          if (!proceed) {
            return;
          }
        }
      }

      const normalizedRewards = getNormalizedVoucherRewards(data, isEdit);
      let savedVoucherId = voucherId || '';
      const showVoucherFromDateValue = getShowVoucherFromDateValue(data, showVoucherFromDateIncludeTime);
      const { voucher_user_expires_at, voucher_user_expires_in_days } = voucherUserExpiryToApiPayload(
        data.voucherUserExpiresAt,
        data.voucherUserExpiresInDays,
        limitVoucherUserExpiryDays,
      );

      if (isEdit) {
        const payload: UpdateVoucherPayload = {
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_call_to_action: data.voucherCallToAction.trim() ? data.voucherCallToAction.trim() : null,
          voucher_call_to_action_url: data.voucherCallToActionUrl.trim() ? data.voucherCallToActionUrl.trim() : null,
          voucher_presentation_config: getVoucherPresentationConfig(data),
          voucher_breakdown_title: data.voucherBreakdownTitle.trim() ? data.voucherBreakdownTitle.trim() : null,
          voucher_amount_display_override: data.voucherAmountDisplayOverride.trim()
            ? data.voucherAmountDisplayOverride.trim()
            : null,
          voucher_rewards: normalizedRewards,
          voucher_conditions: serializeVoucherConditions(data.voucherConditions, existingVoucherConditionIds),
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at,
          voucher_user_expires_in_days,
          theme: data.theme,
          product_type: data.productType,
          case_id: data.caseId.trim() ? data.caseId.trim().toUpperCase() : null,
          reason: data.reason.trim() ? data.reason.trim() : null,
          reward_kind: data.rewardKind,
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          voucher_name: data.voucherName.trim(),
          brand: brand,
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          ...(data.voucherCallToAction.trim() ? { voucher_call_to_action: data.voucherCallToAction.trim() } : {}),
          ...(data.voucherCallToActionUrl.trim()
            ? { voucher_call_to_action_url: data.voucherCallToActionUrl.trim() }
            : {}),
          voucher_presentation_config: getVoucherPresentationConfig(data),
          ...(data.voucherBreakdownTitle.trim() ? { voucher_breakdown_title: data.voucherBreakdownTitle.trim() } : {}),
          ...(data.voucherAmountDisplayOverride.trim()
            ? { voucher_amount_display_override: data.voucherAmountDisplayOverride.trim() }
            : {}),
          voucher_rewards: normalizedRewards,
          voucher_conditions: serializeVoucherConditions(data.voucherConditions, new Set()),
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at: voucher_user_expires_at ?? undefined,
          voucher_user_expires_in_days: voucher_user_expires_in_days ?? undefined,
          theme: data.theme,
          product_type: data.productType,
          ...(data.caseId.trim() ? { case_id: data.caseId.trim().toUpperCase() } : {}),
          ...(data.reason.trim() ? { reason: data.reason.trim() } : {}),
          reward_kind: data.rewardKind,
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        const resolvedExpiredAt = expiredAt.trim()
          ? normalizeToISODate(expiredAt)
          : normalizeToISODate(
              getDefaultAddVoucherUsersExpiryDate({
                voucherUserExpiresAt: data.voucherUserExpiresAt,
                voucherUserExpiresInDays: limitVoucherUserExpiryDays ? data.voucherUserExpiresInDays : '',
              }),
            );
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          expired_at: resolvedExpiredAt,
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }

      reset(data);
      if (isEdit) {
        initialInternalFieldsRef.current = {
          caseId: data.caseId.trim(),
          reason: data.reason.trim(),
        };
      }
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });

      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (isEdit && voucherId) {
        queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
      }

      if (!isEdit && savedVoucherId) {
        bypassUnsavedNavigationBlockRef.current = true;
        replace(
          { pathname: `/marketing/vouchers/${savedVoucherId}` },
          { fromVoucherCreate: { voucherName: data.voucherName.trim() } },
        );
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${formatVoucherSaveFailure(error)}`, { variant: 'error' });
    }
  };

  // Text replacement errors can sit inside collapsed sections; open them on every failed save so it isn't silent.
  const onInvalid = (errors: FieldErrors<VoucherFormValues>) => {
    if (errors.voucherDescription) {
      setDescriptionAccordionExpanded(true);
    }
    if (hasAdvancedPresentationFieldError(errors)) {
      setAdvancedPresentationAccordionExpanded(true);
    }
  };

  const handleSaveVoucher = handleSubmit(onSubmit, onInvalid);

  const applyAiVoucherPayload = (payload: CreateVoucherPayload | UpdateVoucherPayload) => {
    const applied: Array<keyof VoucherFormValues> = [];
    const updates: Partial<VoucherFormValues> = {};
    const setAiField = <Name extends keyof VoucherFormValues>(name: Name, value: VoucherFormValues[Name]) => {
      if (Object.prototype.hasOwnProperty.call(manualEditsRef.current, name)) {
        return;
      }
      updates[name] = value;
      applied.push(name);
    };
    if ('voucher_name' in payload && payload.voucher_name) {
      setAiField('voucherName', payload.voucher_name);
    }
    if (payload.voucher_title !== undefined) {
      setAiField('voucherTitle', payload.voucher_title);
    }
    if (payload.voucher_description !== undefined) {
      setAiField('voucherDescription', payload.voucher_description);
    }
    if (payload.voucher_call_to_action !== undefined) {
      setAiField('voucherCallToAction', payload.voucher_call_to_action ?? '');
    }
    if (payload.voucher_call_to_action_url !== undefined) {
      setAiField('voucherCallToActionUrl', payload.voucher_call_to_action_url ?? '');
    }
    if (payload.voucher_breakdown_title !== undefined) {
      setAiField('voucherBreakdownTitle', payload.voucher_breakdown_title ?? '');
    }
    if (payload.voucher_amount_display_override !== undefined) {
      setAiField('voucherAmountDisplayOverride', String(payload.voucher_amount_display_override ?? ''));
    }
    if (payload.voucher_presentation_config) {
      const config = payload.voucher_presentation_config;
      if (config.showCallToActionButtonInDetails !== undefined) {
        setAiField('showCallToActionButtonInDetails', config.showCallToActionButtonInDetails);
      }
      if (config.showConditionEvaluationWhenFailing !== undefined) {
        setAiField('showConditionEvaluationWhenFailing', config.showConditionEvaluationWhenFailing);
      }
      if (config.showConditionEvaluationWhenPassing !== undefined) {
        setAiField('showConditionEvaluationWhenPassing', config.showConditionEvaluationWhenPassing);
      }
    }
    if (payload.voucher_rewards !== undefined) {
      setAiField(
        'voucherRewards',
        payload.voucher_rewards.map((reward) => ({ ...reward, id_voucher_reward: reward.id_voucher_reward || uuid() })),
      );
    }
    if (payload.voucher_conditions !== undefined) {
      setAiField(
        'voucherConditions',
        payload.voucher_conditions.map((condition) => ({
          ...condition,
          id_voucher_condition: condition.id_voucher_condition || uuid(),
        })),
      );
    }
    if (payload.voucher_toggles !== undefined) {
      setAiField('voucherToggles', payload.voucher_toggles);
    }
    if (payload.show_voucher_from_date !== undefined) {
      const date = getShowVoucherFromFieldsFromApi(payload.show_voucher_from_date);
      setAiField('showVoucherFromDate', date.value);
      setShowVoucherFromDateIncludeTime(date.includeTime);
    }
    if (payload.voucher_user_expires_at !== undefined) {
      setAiField(
        'voucherUserExpiresAt',
        payload.voucher_user_expires_at ? isoToSydneyDate(payload.voucher_user_expires_at) : '',
      );
    }
    if (payload.voucher_user_expires_in_days !== undefined) {
      setAiField(
        'voucherUserExpiresInDays',
        payload.voucher_user_expires_in_days == null ? '' : String(payload.voucher_user_expires_in_days),
      );
      if (!Object.prototype.hasOwnProperty.call(manualEditsRef.current, 'voucherUserExpiresInDays')) {
        const hasExpiryDays = payload.voucher_user_expires_in_days != null;
        limitVoucherUserExpiryDaysRef.current = hasExpiryDays;
        setLimitVoucherUserExpiryDays(hasExpiryDays);
      }
    }
    if (payload.theme !== undefined) {
      setAiField('theme', payload.theme);
    }
    if (payload.product_type !== undefined) {
      setAiField('productType', payload.product_type);
    }
    if (payload.case_id !== undefined) {
      setAiField('caseId', payload.case_id ?? '');
    }
    if (payload.reason !== undefined) {
      setAiField('reason', payload.reason ?? '');
    }
    if (payload.reward_kind !== undefined) {
      setAiField('rewardKind', payload.reward_kind);
    }
    reset({ ...getValues(), ...updates }, { keepDefaultValues: true, keepErrors: true, keepTouched: true });
    void trigger(applied);
    if (applied.includes('voucherDescription')) {
      setDescriptionAccordionExpanded(true);
    }
    if (applied.some((field) => ['voucherRewards', 'voucherConditions', 'voucherToggles'].includes(field))) {
      setStatusContextExpanded(true);
    }
    if (
      applied.some((field) =>
        [
          'voucherCallToAction',
          'voucherCallToActionUrl',
          'voucherBreakdownTitle',
          'voucherAmountDisplayOverride',
        ].includes(field),
      )
    ) {
      setAdvancedPresentationAccordionExpanded(true);
    }
    setAiFields(applied);
  };

  const handleGenerateAiVoucher = async () => {
    const enteredName = getValues('voucherName').trim();
    if ((isEdit && !enteredName) || !aiRequest.trim()) {
      return;
    }
    const codeName = enteredName || generateAiCodeName('voucher');
    if (!enteredName) {
      setValue('voucherName', codeName, { shouldDirty: true, shouldValidate: true });
    }
    const currentValues = getValues();
    for (const field of Object.keys(dirtyFields) as Array<keyof VoucherFormValues>) {
      if (!aiFields.includes(field)) {
        manualEditsRef.current[field] = currentValues[field];
      }
    }
    const nextConversation: Array<AiCodeMessage> = [...aiConversation, { role: 'user', content: aiRequest.trim() }];
    setAiProgress([]);
    try {
      const proposal = await aiGenerate.mutateAsync({
        context: {
          kind: 'voucher',
          operation: isEdit ? 'update' : 'create',
          codeName,
          brand,
          request: aiRequest.trim(),
          conversation: nextConversation,
          previousProposal:
            aiPreviousProposal?.codeName === codeName &&
            aiPreviousProposal.brand === brand &&
            aiPreviousProposal.operation === (isEdit ? 'update' : 'create')
              ? aiPreviousProposal.payload
              : null,
          manualEdits: { ...manualEditsRef.current },
          formDraft: { ...currentValues },
        },
        onProgress: (event) => setAiProgress((current) => [...current, event]),
      });
      if (getValues('voucherName').trim() !== codeName) {
        aiGenerate.reset();
        enqueueSnackbar('Voucher name changed while AI was preparing the proposal. Generate again.', {
          variant: 'warning',
        });
        return;
      }
      setAiConversation([
        ...nextConversation,
        {
          role: 'assistant',
          content:
            proposal.type === 'question' ? [proposal.summary, ...proposal.questions].join('\n') : proposal.summary,
        },
      ]);
      setAiRequest('');
      if (proposal.type === 'question') {
        return;
      }
      setAiPreviousProposal({
        codeName,
        brand,
        operation: isEdit ? 'update' : 'create',
        payload: proposal.payload as CreateVoucherPayload | UpdateVoucherPayload,
      });
      if (proposal.warnings.length === 0 && proposal.errors.length === 0) {
        applyAiVoucherPayload(proposal.payload);
      }
    } catch {
      setAiProgress((current) => {
        const last = current[current.length - 1];
        return last?.status === 'running' ? [...current.slice(0, -1), { ...last, status: 'failed' }] : current;
      });
      // The generation error is displayed in the form.
    }
  };

  const handleCancelDelete = () => {
    if (!deleteVoucherMutation.isPending) {
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!voucherId) {
      enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
      return;
    }
    try {
      await deleteVoucherMutation.mutateAsync({ voucherId });
      enqueueSnackbar(`Deleted voucher ${voucherNameValue || voucherId}.`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      queryClient.removeQueries({ queryKey: ['voucher', voucherId] });
      setDeleteDialogOpen(false);
      bypassUnsavedNavigationBlockRef.current = true;
      push('/marketing/vouchers');
    } catch (error) {
      enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  const createdVoucherName = fromCreate?.voucherName?.trim();
  const requiresPromoAdminForExpiry =
    !isEdit &&
    isVoucherUserExpiryOverTwelveMonths({
      mode: limitVoucherUserExpiryDays ? 'days' : 'date',
      voucherUserExpiresAt: watchedVoucherUserExpiresAt ?? '',
      voucherUserExpiresInDays: watchedVoucherUserExpiresInDays ?? '',
    });
  const renderSectionUnsavedChangesAlert = (hasUnsavedChanges: boolean) => {
    if (!hasUnsavedChanges) {
      return null;
    }

    return (
      <Alert
        severity="info"
        sx={{ mb: 2, alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" onClick={handleSaveVoucher} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        }
      >
        Unsaved changes
      </Alert>
    );
  };

  const aiFieldStyles = Object.fromEntries(
    aiFields.map((field): [string, SystemStyleObject<Theme>] => [
      `& .MuiFormControl-root:has([name="${field}"])`,
      {
        borderRadius: 1,
        outline: '2px solid',
        outlineColor: 'info.main',
        bgcolor: 'action.hover',
        animation: 'aiFieldPulse 2s ease-out 2',
      },
    ]),
  );

  return (
    <Container
      sx={{
        '@keyframes aiFieldPulse': {
          '0%': { boxShadow: (theme: Theme) => `0 0 0 0 ${alpha(theme.palette.info.main, 0.55)}` },
          '70%': { boxShadow: (theme: Theme) => `0 0 0 8px ${alpha(theme.palette.info.main, 0)}` },
          '100%': { boxShadow: 'none' },
        },
        ...aiFieldStyles,
      }}
    >
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap" spacing={2} useFlexGap>
          {requiresPromoAdminForExpiry ? (
            <PromoAdminRequiredIndicator action="Creating this long-lasting voucher" />
          ) : null}
          <VoucherFormNavigation
            isSubmitting={isSubmitting}
            hasUnsavedChanges={isDirty}
            isEdit={isEdit}
            showAdvanceDescription={showAdvanceDescription}
            onBack={() => push('/marketing/vouchers')}
            onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
            onOpenClone={isEdit && voucherId ? () => setCloneDialogOpen(true) : undefined}
            showExtendedWarnings={showExtendedWarnings}
            onToggleExtendedWarnings={
              isEdit && voucherId ? () => setShowExtendedWarnings(!showExtendedWarnings) : undefined
            }
          />
          {isEdit && voucher && (voucherAuditFields.createdByUserId || voucherAuditFields.modifiedByUserId) ? (
            <Stack alignItems="flex-end" spacing={0.25} sx={{ textAlign: 'right' }}>
              {voucherAuditFields.createdByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Created by{' '}
                  <Link to={`/users/${voucherAuditFields.createdByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.createdByUserId)?.full_name ??
                      voucherAuditFields.createdByUserId}
                  </Link>
                  {voucherAuditFields.createdAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.createdAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {voucherAuditFields.modifiedByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Last modified by{' '}
                  <Link to={`/users/${voucherAuditFields.modifiedByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.modifiedByUserId)?.full_name ??
                      voucherAuditFields.modifiedByUserId}
                  </Link>
                  {voucherAuditFields.updatedAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.updatedAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </PageHeader>

      <AiToolsSection title="Voucher tools">
        <PromoFeature
          featureKey="ai-promo-tools-access"
          presentation="raw"
          accessMode="show-warning"
          expandLabel={isEdit ? 'Describe the update you want to make' : 'Describe the voucher you want to create'}
        >
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AutoFixHighIcon color="primary" />
                <Typography variant="h6">
                  {isEdit ? 'Describe the update you want to make' : 'Describe the voucher you want to create'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {aiWorkflow === 'create-or-update' ? 'The voucher name was resolved before opening this form. ' : ''}
              </Typography>
              {hasAiRequestBeenFocused && (
                <Alert severity="info">
                  This workflow will not update or create without your confirmation. The AI will propose changes to the
                  form fields, which you can review and edit before saving.
                </Alert>
              )}
              {aiConversation.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1} aria-label="AI conversation">
                    {aiConversation.map((message, index) => (
                      <Box key={index}>
                        <Typography variant="caption" color="text.secondary">
                          {message.role === 'user' ? 'You' : 'AI'}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
              <Stack spacing={2}>
                <TextField
                  label={
                    aiGenerate.data?.type === 'question'
                      ? 'Reply to the AI'
                      : aiConversation.length > 0
                        ? 'Continue the conversation'
                        : 'Describe what the AI should set'
                  }
                  value={aiRequest}
                  onFocus={() => setHasAiRequestBeenFocused(true)}
                  onChange={(event) => setAiRequest(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  helperText={
                    isEdit
                      ? 'Fill in the voucher name in the form before preparing a proposal.'
                      : 'Leave the voucher name blank to generate one automatically.'
                  }
                />
                <Stack spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    sx={{ flexShrink: 0 }}
                    startIcon={<AutoFixHighIcon />}
                    disabled={!!aiDisabledReason}
                    aria-describedby={aiDisabledReason ? aiDisabledReasonId : undefined}
                    onClick={() => void handleGenerateAiVoucher()}
                  >
                    {aiGenerate.isPending
                      ? 'Preparing...'
                      : aiConversation.length > 0
                        ? 'Send and set fields'
                        : 'Set form fields with AI'}
                  </Button>
                  {aiDisabledReason && (
                    <Typography id={aiDisabledReasonId} variant="body2" color="text.secondary">
                      {aiDisabledReason}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {aiConversation.length === 0 && (
                <PromoAiQueryExamples
                  kind="voucher"
                  disabled={aiGenerate.isPending || isSubmitting || (isEdit && isVoucherLoading)}
                  onSelect={(query) => {
                    setAiRequest(query);
                    setHasAiRequestBeenFocused(true);
                  }}
                />
              )}
              {aiProgress.length > 0 && (
                <AiWorkflowProgress
                  events={aiProgress}
                  validationPassed={
                    aiGenerate.data?.type === 'proposal'
                      ? aiGenerate.data.errors.length === 0 && aiGenerate.data.warnings.length === 0
                      : aiGenerate.data?.type === 'question'
                        ? false
                        : undefined
                  }
                />
              )}
              {aiGenerate.isError && (
                <Alert severity="error">
                  {aiGenerate.error instanceof Error ? aiGenerate.error.message : 'Unable to prepare voucher.'}
                </Alert>
              )}
              {aiGenerate.data?.type === 'question' && (
                <Alert severity="info">
                  <Typography variant="subtitle2">The AI needs more detail</Typography>
                  {aiGenerate.data.questions.map((question) => (
                    <Typography key={question} variant="body2">
                      {question}
                    </Typography>
                  ))}
                  Reply above to continue.
                </Alert>
              )}
              {aiGenerate.data?.type === 'proposal' &&
                (aiGenerate.data.warnings.length > 0 || aiGenerate.data.errors.length > 0) && (
                  <Alert severity="warning">
                    Validation still has issues after {aiGenerate.data.validationAttempts} attempts. Clarify your
                    request or edit the form, then generate again.
                    {[...aiGenerate.data.errors, ...aiGenerate.data.warnings].map((message) => (
                      <Typography key={message} variant="body2">
                        {message}
                      </Typography>
                    ))}
                  </Alert>
                )}
              {aiGenerate.data?.type === 'proposal' &&
                aiGenerate.data.warnings.length === 0 &&
                aiGenerate.data.errors.length === 0 && (
                  <Alert severity="success">
                    <Typography variant="subtitle2">AI set {aiFields.length} fields</Typography>
                    <Typography variant="body2">Review the highlighted values before saving.</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                      {aiFields.map((field) => (
                        <Box key={field} sx={{ px: 1, py: 0.5, border: 1, borderColor: 'info.main', borderRadius: 1 }}>
                          {field.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())}
                        </Box>
                      ))}
                    </Stack>
                  </Alert>
                )}
            </Stack>
          </Paper>
        </PromoFeature>
      </AiToolsSection>

      {(voucherWarnings.length > 0 ||
        lateVoucherUserExpiryGroups.length > 0 ||
        (showExtendedWarnings && extendedWarningsQuery.isLoading) ||
        (showExtendedWarnings && extendedWarningsQuery.error) ||
        (isEdit && voucherId && fromCreate) ||
        clonedFromId) && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {(voucherWarnings.length > 0 ||
            lateVoucherUserExpiryGroups.length > 0 ||
            (showExtendedWarnings && extendedWarningsQuery.isLoading) ||
            (showExtendedWarnings && extendedWarningsQuery.error)) && (
            <Accordion
              expanded={voucherWarningsExpanded}
              onChange={(_, expanded) => setVoucherWarningsExpanded(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography component="span" fontWeight={500}>
                    Voucher warnings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[
                      ...new Set(voucherWarnings.map((warning) => warning.code ?? 'Voucher warning')),
                      ...(lateVoucherUserExpiryGroups.length > 0 ? ['Voucher user expiry after voucher expiry'] : []),
                      ...(showExtendedWarnings && extendedWarningsQuery.isLoading
                        ? ['Loading extended warnings…']
                        : []),
                      ...(showExtendedWarnings && extendedWarningsQuery.error
                        ? ['Could not load extended warnings']
                        : []),
                    ].join(' · ')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  {showExtendedWarnings && extendedWarningsQuery.isLoading && (
                    <Alert severity="info">Loading extended voucher warnings…</Alert>
                  )}
                  {showExtendedWarnings && extendedWarningsQuery.error && (
                    <Alert severity="warning">
                      Could not load extended voucher warnings: {extendedWarningsQuery.error.message}
                    </Alert>
                  )}
                  {voucherWarnings.length > 0 && (
                    <Stack direction="column" spacing={2}>
                      {voucherWarnings.map((warning, index) => (
                        <Alert
                          key={`${warning.code ?? 'voucher-warning'}-${index}`}
                          severity={getVoucherWarningSeverity(warning.level)}
                          title={JSON.stringify(warning, null, 4)}
                        >
                          <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                          {warning.message || 'Please review this warning before proceeding.'}
                          <PromoResolution
                            resolution={warning.resolution}
                            targets={getWarningResolutionTargets(warning)}
                          />
                        </Alert>
                      ))}
                    </Stack>
                  )}
                  {lateVoucherUserExpiryGroups.map((group) => (
                    <Alert
                      key={group.expiredAt}
                      severity="warning"
                      action={
                        <Button
                          color="primary"
                          variant="text"
                          onClick={() => {
                            setUsersExpanded(true);
                            setBulkExpiryExpanded(true);
                            if (usersExpanded && bulkExpiryExpanded) {
                              scrollToBulkExpiryForm();
                            }
                          }}
                        >
                          Update user expiry
                        </Button>
                      }
                    >
                      <AlertTitle>Voucher user expiry after voucher expiry</AlertTitle>
                      {group.voucherUsers.length} voucher user{group.voucherUsers.length === 1 ? '' : 's'}{' '}
                      {dayjs(group.expiredAt).isAfter(dayjs())
                        ? group.voucherUsers.length === 1
                          ? 'expires'
                          : 'expire'
                        : 'expired'}{' '}
                      on {dayjs(group.expiredAt).format('D MMM YYYY, h:mm a')}.
                      {voucher?.voucherUserExpiresAt && (
                        <>
                          {' '}
                          This is approximately{' '}
                          {dayjs
                            .duration(dayjs(group.expiredAt).diff(dayjs(voucher.voucherUserExpiresAt)))
                            .humanize()}{' '}
                          after the voucher&apos;s configured user expiry of{' '}
                          {dayjs(voucher.voucherUserExpiresAt).format('D MMM YYYY, h:mm a')}.
                        </>
                      )}{' '}
                      Review these assignments to confirm the later expiry is intentional. Select &quot;Update user
                      expiry&quot; to open the bulk expiry form for all unused voucher users assigned to this voucher.
                    </Alert>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
          {isEdit && voucherId && fromCreate ? (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              onClose={() => {
                const state = { ...((location.state as Record<string, unknown> | null) ?? {}) };
                delete state.fromVoucherCreate;
                replace(
                  { hash: location.hash, pathname: location.pathname, search: location.search },
                  Object.keys(state).length > 0 ? state : null,
                );
              }}
            >
              <Typography variant="body2" component="div">
                Voucher <strong>{createdVoucherName || voucherNameValue || voucherId}</strong> was created.{' '}
                <Link to={`/marketing/vouchers/${voucherId}`}>Edit voucher</Link>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {voucherId}
              </Typography>
            </Alert>
          ) : null}
          {clonedFromId ? (
            <Typography variant="body2" color="text.secondary">
              Cloned from:{' '}
              <Link to={`/marketing/vouchers/${clonedFromId}`}>{clonedFromVoucherName || clonedFromId}</Link>
            </Typography>
          ) : null}
        </Stack>
      )}

      {isEdit && voucherId && (
        <VoucherCloneDialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          sourceVoucherId={voucherId}
          sourceVoucherDisplayName={voucherNameValue.trim() || undefined}
          sourceVoucherRewards={voucherRewards}
          sourceVoucherCurrencyCode={currencyCode}
          suggestedNewVoucherName={buildSuggestedClonedVoucherName(voucherNameValue)}
          clonedFromVoucherId={voucher?.clonedFromId}
          onCloned={(newVoucherId) => {
            queryClient.invalidateQueries({ queryKey: ['voucher', newVoucherId] });
            push(`/marketing/vouchers/${newVoucherId}`);
          }}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {voucherNameValue || voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelDelete} disabled={deleteVoucherMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDelete}
            disabled={deleteVoucherMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={2}>
        {isEdit && voucherId ? (
          <Box component="section" aria-label="Voucher history" sx={{ order: -2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              History
            </Typography>
            <AuditEventSummary title={voucherNameValue || voucherId} modelType="voucher" modelId={voucherId} />
          </Box>
        ) : null}
        <Accordion
          expanded={presentationExpanded}
          onChange={(_, expanded) => setPresentationExpanded(expanded)}
          slotProps={{ transition: { onEntered: () => navigateToResolutionField() } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ py: 0.5, pr: 1, width: '100%' }}>
              <Stack spacing={1}>
                <Box>
                  <Typography component="span" fontWeight={500}>
                    Presentation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                    {voucherNameValue.trim() || 'Create Voucher'}
                    {' · '}
                    {isPreviewPending
                      ? 'Loading preview'
                      : isPreviewError || previewLocalError
                        ? 'Preview needs attention'
                        : publicPreviewVoucher
                          ? `Showing ${previewCurrencyCode} in ${selectedPreviewLocaleLabel}`
                          : 'No preview loaded'}
                  </Typography>
                </Box>
                {publicPreviewVoucher ? (
                  <VoucherPublicPreviewCard voucher={publicPreviewVoucher} showDetails={false} />
                ) : null}
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <Box component="section" aria-labelledby="voucher-preview-controls-heading">
                <Typography id="voucher-preview-controls-heading" variant="subtitle2" sx={{ mb: 1 }}>
                  Preview controls
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Button variant="text" onClick={() => handleRefreshPreview()} disabled={isPreviewPending}>
                    Refresh preview
                  </Button>
                  <Accordion
                    defaultExpanded={false}
                    disableGutters
                    variant="outlined"
                    sx={{ width: { sm: 320 } }}
                    slotProps={{ transition: { unmountOnExit: true } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">Preview options</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          Best-effort mock display; may differ from the customer portal.
                        </Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                            Preview currency
                          </Typography>
                          <RadioGroup
                            aria-label="Preview currency"
                            value={previewCurrencyCode}
                            onChange={(_, currency) => setPreviewCurrencyCode(currency)}
                            sx={{ flexDirection: 'column', gap: 0.5 }}
                          >
                            {resolvedVoucherPreviewCurrencyOptions.map((currency) => (
                              <FormControlLabel
                                key={currency}
                                value={currency}
                                label={currency}
                                control={<Radio disabled={isPreviewPending} />}
                                sx={{ mr: 0 }}
                              />
                            ))}
                          </RadioGroup>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                            Preview locale
                          </Typography>
                          <RadioGroup
                            aria-label="Preview locale"
                            value={previewLocale}
                            onChange={(_, locale) => setPreviewLocale(locale)}
                            sx={{ flexDirection: 'column', gap: 0.5 }}
                          >
                            {[
                              ...voucherPreviewLocaleOptions.common,
                              ...(showAllPreviewLocales ? voucherPreviewLocaleOptions.additional : []),
                            ].map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                label={`${option.label} (${option.value})`}
                                control={<Radio disabled={isPreviewPending} />}
                                sx={{ mr: 0 }}
                              />
                            ))}
                          </RadioGroup>
                          {voucherPreviewLocaleOptions.additional.length > 0 && !showAllPreviewLocales ? (
                            <Button variant="text" size="small" onClick={() => setShowAllPreviewLocales(true)}>
                              Show more languages
                            </Button>
                          ) : null}
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Box>
              <Box component="section" aria-labelledby="voucher-preview-feedback-heading">
                <Typography id="voucher-preview-feedback-heading" variant="subtitle2" sx={{ mb: 1 }}>
                  Preview feedback and text replacements
                </Typography>
                <VoucherPreviewContent
                  localError={previewLocalError}
                  isError={isPreviewError}
                  errorHeadline={previewErrorHeadline}
                  errorDetailBlock={previewErrorDetailBlock}
                  publicVoucher={publicPreviewVoucher}
                  textReplacements={previewTextReplacements}
                  isPending={isPreviewPending}
                  localeLabel={selectedPreviewLocaleLabel}
                  untranslatedFields={untranslatedPreviewFields}
                  emptyMessage="The preview will load when the required voucher fields are available."
                  loadingMessage="Refreshing preview..."
                  showPreviewCard={false}
                />
              </Box>
              <Box component="section" aria-labelledby="voucher-display-basics-heading">
                <Typography id="voucher-display-basics-heading" variant="subtitle2" sx={{ mb: 1 }}>
                  Customer-facing display
                </Typography>
                <Stack spacing={2}>
                  <Controller
                    name="voucherTitle"
                    control={control}
                    rules={{
                      validate: (value) =>
                        isEdit || value.trim()
                          ? validateVoucherTextReplacements(value)
                          : 'Voucher title is required when creating a voucher.',
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Voucher Title"
                        required={!isEdit}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ||
                          (showAdvanceDescription
                            ? getVoucherTemplatedFieldLongDescription(
                                'voucher_title',
                                isEdit
                                  ? 'Optional title displayed for this voucher to the user when available.'
                                  : 'Title displayed for this voucher to the user.',
                              )
                            : undefined)
                        }
                        fullWidth
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      ref={productTypeRef}
                      sx={
                        resolutionField === 'productType'
                          ? { outline: '2px solid', outlineColor: 'info.main', borderRadius: 1 }
                          : undefined
                      }
                    >
                      <Controller
                        name="productType"
                        control={control}
                        render={({ field }) => {
                          const row = voucherProductTypeMenuRows.find((r) => r.key === field.value);
                          const descriptionHint =
                            showAdvanceDescription &&
                            (row?.description?.trim() ||
                              'Which product type this voucher aligns with (for presentation).');
                          return (
                            <TextField
                              {...field}
                              select
                              label="Product Type"
                              helperText={descriptionHint || undefined}
                              fullWidth
                              disabled={isSubmitting || (isPromoMetaLoading && voucherProductTypeMenuRows.length === 0)}
                            >
                              {voucherProductTypeMenuRows.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="theme"
                        control={control}
                        render={({ field }) => {
                          const row = voucherThemeMenuRows.find((r) => r.key === field.value);
                          const descriptionHint =
                            showAdvanceDescription &&
                            (row?.description?.trim() || 'Optional theme for the voucher display styling.');
                          return (
                            <TextField
                              {...field}
                              select
                              label="Theme"
                              helperText={descriptionHint || undefined}
                              fullWidth
                              disabled={isSubmitting || (isPromoMetaLoading && voucherThemeMenuRows.length === 0)}
                            >
                              {voucherThemeMenuRows.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
              <Accordion
                expanded={advancedPresentationAccordionExpanded}
                onChange={(_, nextExpanded) => setAdvancedPresentationAccordionExpanded(nextExpanded)}
                disableGutters
                variant="outlined"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography component="span" fontWeight={500}>
                      Advanced voucher presentation
                    </Typography>
                    <Typography
                      variant="body2"
                      color={hasAdvancedPresentationError ? 'error' : 'text.secondary'}
                      sx={accordionSubtitleSx}
                    >
                      {hasAdvancedPresentationError
                        ? 'Fix the text replacement errors in this section.'
                        : 'Control customer-facing voucher details and condition evaluation.'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1}>
                    <Controller
                      name="voucherCallToAction"
                      control={control}
                      rules={{ validate: validateVoucherTextReplacements }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Voucher Call To Action"
                          error={Boolean(fieldState.error)}
                          helperText={
                            fieldState.error?.message ??
                            (showAdvanceDescription
                              ? getVoucherTemplatedFieldLongDescription(
                                  'voucher_call_to_action',
                                  'Optional. Overrides the default call-to-action text shown with the voucher before redemption.',
                                )
                              : 'Optional. Overrides the default call-to-action text shown with the voucher before redemption.')
                          }
                          fullWidth
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Controller
                      name="voucherCallToActionUrl"
                      control={control}
                      rules={{ validate: validateVoucherTextReplacements }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Voucher Call To Action URL"
                          error={Boolean(fieldState.error)}
                          type="url"
                          helperText={
                            fieldState.error?.message ??
                            (showAdvanceDescription
                              ? getVoucherTemplatedFieldLongDescription(
                                  'voucher_call_to_action_url',
                                  'Optional. Destination link for the voucher call-to-action.',
                                )
                              : 'Optional. Destination link for the voucher call-to-action.')
                          }
                          fullWidth
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Controller
                      name="voucherBreakdownTitle"
                      control={control}
                      rules={{ validate: validateVoucherTextReplacements }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Voucher Breakdown Title"
                          error={Boolean(fieldState.error)}
                          helperText={
                            fieldState.error?.message ??
                            (showAdvanceDescription
                              ? getVoucherTemplatedFieldLongDescription(
                                  'voucher_breakdown_title',
                                  'Optional. Names this voucher on the checkout price breakdown row instead of the default "Voucher discount". Keep it to a few words, no amount. Leave blank for the default, which stays translated for non-English customers.',
                                )
                              : 'Optional. Names this voucher on the checkout price breakdown row instead of the default "Voucher discount". Keep it to a few words, no amount. Leave blank for the default, which stays translated for non-English customers.')
                          }
                          fullWidth
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Controller
                      name="voucherAmountDisplayOverride"
                      control={control}
                      rules={{ validate: validateVoucherTextReplacements }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Voucher Amount Display Override"
                          error={Boolean(fieldState.error)}
                          helperText={
                            fieldState.error?.message ??
                            (showAdvanceDescription
                              ? getVoucherTemplatedFieldLongDescription(
                                  'voucher_amount_display_override',
                                  'Optional amount label shown to customers instead of the computed value. Use [none] to show no amount label.',
                                )
                              : 'Optional customer-facing amount label override. Use [none] to hide it.')
                          }
                          fullWidth
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Controller
                      name="showCallToActionButtonInDetails"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label="Show the call-to-action button in voucher details"
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(_, checked) => field.onChange(checked)}
                              disabled={isSubmitting}
                            />
                          }
                        />
                      )}
                    />
                    <Controller
                      name="showConditionEvaluationWhenFailing"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label="Show condition evaluation when the voucher cannot be used"
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(_, checked) => field.onChange(checked)}
                              disabled={isSubmitting}
                            />
                          }
                        />
                      )}
                    />
                    <Controller
                      name="showConditionEvaluationWhenPassing"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label="Show condition evaluation when the voucher can be used"
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(_, checked) => field.onChange(checked)}
                              disabled={isSubmitting}
                            />
                          }
                        />
                      )}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <VoucherQuickTest
                key={`${voucherId}-${JSON.stringify(voucher)}-${brand}`}
                voucherId={voucherId}
                brand={brand}
                disabled={isDirty || isSubmitting || isVoucherFetching}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={statusContextExpanded}
          slotProps={{ transition: { onEntered: () => navigateToResolutionField() } }}
          onChange={(_, nextExpanded) => setStatusContextExpanded(nextExpanded)}
          sx={{ order: -1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography component="span" fontWeight={500}>
                {voucherNameValue?.trim() || 'Create Voucher'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                Visibility, voucher-user expiry, rewards, qualification rules, and voucher behaviour.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <Controller
                name="voucherName"
                control={control}
                rules={{
                  validate: (value) => (isEdit ? true : validateVoucherName(value)),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Voucher Name"
                    error={!!fieldState.error}
                    sx={{
                      ...(isEdit
                        ? {
                            '& .MuiInputBase-input': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'currentColor',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'text.secondary',
                            },
                          }
                        : {}),
                    }}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Name displayed for the voucher. This is used during processing and is a fallback value if the voucher title is not set'
                        : !isEdit
                          ? `Voucher name cannot be changed after creation. ${VOUCHER_NAME_PATTERN_MESSAGE}.`
                          : '')
                    }
                    fullWidth
                    required
                    disabled={isSubmitting}
                    slotProps={{
                      input: {
                        readOnly: isEdit,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="voucherDescription"
                control={control}
                rules={{ validate: validateVoucherTextReplacements }}
                render={({ field, fieldState }) => (
                  <Accordion
                    expanded={descriptionAccordionExpanded}
                    onChange={(_, nextExpanded) => setDescriptionAccordionExpanded(nextExpanded)}
                    disableGutters
                    variant="outlined"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ overflow: 'hidden', pr: 1, width: '100%' }}>
                        <Typography component="span" fontWeight={500}>
                          Voucher Description
                        </Typography>
                        <Typography
                          variant="body2"
                          color={fieldState.error ? 'error' : 'text.secondary'}
                          sx={{
                            ...accordionSubtitleSx,
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fieldState.error?.message ?? voucherDescriptionSummaryPreview}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <MarkdownEditor
                        key={descriptionEditorKey}
                        {...field}
                        options={{ onEditorChange: field.onChange }}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ??
                          (showAdvanceDescription
                            ? getVoucherTemplatedFieldLongDescription(
                                'voucher_description',
                                'Optional description shown to the user to explain voucher details. Supports markdown.',
                              )
                            : undefined)
                        }
                        disabled={isSubmitting}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reason"
                      fullWidth
                      required={!isEdit}
                      disabled={isSubmitting}
                      helperText={
                        showAdvanceDescription
                          ? 'Internal note describing why this voucher exists (not shown to customers).'
                          : 'Internal note (not shown to customers).'
                      }
                    />
                  )}
                />
                <Controller
                  name="caseId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Case ID"
                      fullWidth
                      disabled={isSubmitting}
                      helperText={
                        showAdvanceDescription
                          ? 'Internal CaseId (internal only).'
                          : 'Internal only; stored uppercased. (internal only).'
                      }
                    />
                  )}
                />
              </Stack>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Voucher visibility and user expiry</Typography>
                <Typography variant="body2" color="text.secondary">
                  When a user is assigned a voucher, this date will be used to determine the date that is set on the
                  created voucher user. Attempts to assign this voucher after this date will fail. If the voucher has a
                  user expiry DAYS set, the days calculation will be used. (i.e. the voucher will expire for that user
                  after X days)
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Controller
                      name="showVoucherFromDate"
                      control={control}
                      render={({ field }) => {
                        const v = field.value || '';
                        const inputValue = showVoucherFromDateIncludeTime
                          ? v
                            ? v.length === 10
                              ? `${v}T00:00`
                              : v.slice(0, 16)
                            : ''
                          : v
                            ? v.slice(0, 10)
                            : '';
                        return (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                              label="Show Voucher From Date"
                              type={showVoucherFromDateIncludeTime ? 'datetime-local' : 'date'}
                              value={inputValue}
                              onChange={(event) => field.onChange(event.target.value)}
                              InputLabelProps={{ shrink: true }}
                              inputProps={showVoucherFromDateIncludeTime ? { step: 60 } : undefined}
                              helperText={
                                showAdvanceDescription
                                  ? showVoucherFromDateIncludeTime
                                    ? 'Optional date and time (Sydney time) from which this voucher becomes visible to users. Leave empty for immediate visibility.'
                                    : 'Optional: first date this voucher is visible, from midnight Sydney time. Most of the time the start time does not matter—use Set time only when you need a specific time. Leave empty for immediate visibility.'
                                  : undefined
                              }
                              fullWidth
                              disabled={isSubmitting}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={showVoucherFromDateIncludeTime}
                                  onChange={(_, checked) => {
                                    setShowVoucherFromDateIncludeTime(checked);
                                    const current = (getValues('showVoucherFromDate') || '').trim();
                                    if (checked) {
                                      // Empty means "immediate visibility" — do not invent today's date at midnight.
                                      if (!current) {
                                        field.onChange('');
                                        return;
                                      }
                                      const datePart =
                                        current.length >= 10 ? current.slice(0, 10) : dayjs().format('YYYY-MM-DD');
                                      field.onChange(`${datePart}T00:00`);
                                    } else {
                                      const datePart =
                                        current.length >= 16
                                          ? current.slice(0, 10)
                                          : current.length === 10
                                            ? current
                                            : '';
                                      field.onChange(datePart);
                                    }
                                  }}
                                  disabled={isSubmitting}
                                />
                              }
                              label="Set time"
                              sx={{
                                m: 0,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                alignSelf: 'center',
                                pr: 0.5,
                              }}
                            />
                          </Box>
                        );
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Controller
                      name="voucherUserExpiresAt"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const at = (value || '').trim();
                          if (!at) {
                            return limitVoucherUserExpiryDaysRef.current
                              ? true
                              : 'Enter a voucher user expiry date, or set a day limit after assignment.';
                          }
                          const showFromRaw = (getValues('showVoucherFromDate') || '').trim();
                          if (showFromRaw) {
                            const startDatePart = showFromRaw.length >= 10 ? showFromRaw.slice(0, 10) : showFromRaw;
                            const expiryDatePart = at.length >= 10 ? at.slice(0, 10) : at;
                            if (dayjs(startDatePart).isAfter(dayjs(expiryDatePart), 'day')) {
                              return 'Expiry date must be on or after the voucher start date.';
                            }
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          inputRef={expiryRef}
                          label="Voucher user expires at"
                          required={!limitVoucherUserExpiryDays}
                          type="date"
                          value={field.value ? dayjs(field.value).format('YYYY-MM-DD') : ''}
                          onChange={(event) => {
                            const localValue = event.target.value;
                            if (!localValue) {
                              field.onChange('');
                              return;
                            }
                            field.onChange(dayjs(localValue).format('YYYY-MM-DD'));
                          }}
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(fieldState.error)}
                          helperText={
                            fieldState.error?.message ||
                            (showAdvanceDescription
                              ? 'Hard end date for this voucher, from midnight Sydney time.'
                              : undefined)
                          }
                          fullWidth
                          disabled={isSubmitting}
                          sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={limitVoucherUserExpiryDays}
                      onChange={(_, checked) => {
                        limitVoucherUserExpiryDaysRef.current = checked;
                        setLimitVoucherUserExpiryDays(checked);
                        if (!checked) {
                          setValue('voucherUserExpiresInDays', '', { shouldDirty: true, shouldValidate: true });
                        }
                        void trigger(['voucherUserExpiresAt', 'voucherUserExpiresInDays']);
                      }}
                      disabled={isSubmitting}
                    />
                  }
                  label={`Make this voucher expire a set number of days after assignment (instead of a fixed date define above). This 'rolling' voucher user expiry allows for 'short-lived'/'urgent-use' vouchers that promote the user to redeem quickly.`}
                  sx={{ alignSelf: 'flex-start' }}
                />
                {limitVoucherUserExpiryDays && (
                  <Box sx={{ maxWidth: { md: '50%' } }}>
                    <Controller
                      name="voucherUserExpiresInDays"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!limitVoucherUserExpiryDaysRef.current) {
                            return true;
                          }
                          const trimmed = (value || '').trim();
                          if (!trimmed) {
                            return 'Enter the number of days after assignment.';
                          }
                          const parsed = Number.parseInt(trimmed, 10);
                          if (!Number.isInteger(Number(trimmed))) {
                            return 'Enter a valid whole number';
                          }
                          if (parsed <= 0) {
                            return 'Must be greater than 0';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Voucher user expires in (days)"
                          required
                          type="number"
                          error={Boolean(fieldState.error)}
                          inputProps={{ min: 1, step: 1 }}
                          helperText={
                            fieldState.error?.message ||
                            (showAdvanceDescription
                              ? 'The voucher user expires this many days after assignment.'
                              : undefined)
                          }
                          fullWidth
                          disabled={isSubmitting}
                          sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                        />
                      )}
                    />
                  </Box>
                )}
                <VoucherLongExpiryPromoAdminAlert
                  isCreate={!isEdit}
                  mode={limitVoucherUserExpiryDays ? 'days' : 'date'}
                  voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
                  voucherUserExpiresInDays={limitVoucherUserExpiryDays ? (watchedVoucherUserExpiresInDays ?? '') : ''}
                />
              </Stack>
              <Accordion
                expanded={rewardsExpanded}
                onChange={(_, expanded) => setRewardsExpanded(expanded)}
                slotProps={{ transition: { onEntered: () => navigateToResolutionField() } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography component="span" fontWeight={500}>
                      Rewards and conditions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                      How much the voucher is worth, and under what conditions it can be used.
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={3}>
                    <Box
                      ref={rewardsRef}
                      sx={
                        aiFields.includes('voucherRewards')
                          ? {
                              outline: '2px solid',
                              outlineColor: 'info.main',
                              borderRadius: 1,
                              animation: 'aiFieldPulse 2s ease-out 2',
                            }
                          : undefined
                      }
                    >
                      <PageSubheader title="Rewards">
                        {showAdvanceDescription && (
                          <Box component="span" color="text.secondary" alignItems="left">
                            The amount the voucher is worth, and for which products. Only one reward is applied to an
                            order.
                          </Box>
                        )}
                      </PageSubheader>
                      {renderSectionUnsavedChangesAlert(hasUnsavedRewardChanges)}
                      <Box sx={{ mb: 2, maxWidth: { sm: 480 } }}>
                        <Controller
                          name="rewardKind"
                          control={control}
                          render={({ field }) => {
                            const helperPrimary = showAdvanceDescription
                              ? 'Reward kind options (key, name, description, admin visibility) come from promo meta.'
                              : undefined;
                            return (
                              <PromoFeature
                                presentation="radioSelect"
                                label="Reward kind"
                                disabledReason={
                                  isSubmitting
                                    ? 'Wait until the voucher has finished saving.'
                                    : 'Reward kinds are still loading.'
                                }
                                value={field.value}
                                onChange={field.onChange}
                                disabled={
                                  isSubmitting || (isPromoMetaLoading && voucherRewardKindMenuRows.length === 0)
                                }
                                options={voucherRewardKindMenuRows.map((option) => ({
                                  value: option.key,
                                  label: option.name,
                                  description: option.description || helperPrimary,
                                  featureKey:
                                    option.key === 'provide_credit'
                                      ? 'fixed-credit-voucher-rewards'
                                      : option.key === 'provide_credit_percentage'
                                        ? 'percentage-credit-voucher-rewards'
                                        : undefined,
                                }))}
                              />
                            );
                          }}
                        />
                      </Box>
                      <Controller
                        name="voucherRewards"
                        control={control}
                        render={({ field }) => (
                          <VoucherRewards
                            rewards={field.value}
                            navigationTarget={
                              resolutionField === 'productType' ||
                              resolutionField === 'expiry' ||
                              resolutionField === 'conditions'
                                ? undefined
                                : rewardNavigationTarget
                            }
                            rewardKind={watchedRewardKind}
                            conditions={voucherConditions}
                            onChange={field.onChange}
                            currencyCode={currencyCode}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    </Box>
                    <Box
                      ref={conditionsRef}
                      tabIndex={-1}
                      sx={
                        aiFields.includes('voucherConditions')
                          ? {
                              outline: '2px solid',
                              outlineColor: 'info.main',
                              borderRadius: 1,
                              animation: 'aiFieldPulse 2s ease-out 2',
                            }
                          : undefined
                      }
                    >
                      <PageSubheader title="Conditions" />
                      {renderSectionUnsavedChangesAlert(hasUnsavedConditionChanges)}
                      <Controller
                        name="voucherConditions"
                        control={control}
                        render={({ field }) => (
                          <VoucherConditions
                            conditions={field.value}
                            rewards={voucherRewards}
                            onChange={field.onChange}
                            showAdvanceDescription={showAdvanceDescription}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    </Box>
                    <Accordion defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Voucher logic</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Stack direction="column" gap={1.5}>
                          <Stack direction="column" gap={0.5}>
                            <Typography variant="subtitle2">Voucher discount calculation</Typography>
                            <Typography variant="caption">
                              Checkout runs /discount with the current cart/order payload, user, request source, and
                              selected voucher ids. The request and response are saved for the Promo Requests admin
                              view, including voucher names, offer ids, amount, and the first error code/message when
                              the calculation fails.
                            </Typography>
                            <Typography variant="caption">
                              Each reward row below is evaluated against the order. Products define which items can
                              receive voucher value; Min Spend Products define which items are counted against the
                              reward's Min Spend value.
                            </Typography>
                            <Typography variant="caption">
                              If multiple reward rows qualify, the calculator chooses the best eligible reward for the
                              voucher, accounts for any partial redemption amount already used, and returns the voucher
                              amount, display voucher, voucher totals, credit totals, and item-level vouchersApplied
                              data for checkout/payment.
                            </Typography>
                            <Typography variant="caption">
                              Payment logging runs /vouchers/log after the order is placed. It revalidates the submitted
                              vouchersApplied payload, marks voucher users as used or increments amount used, and writes
                              Voucher Logs for admin visibility.
                            </Typography>
                          </Stack>

                          <Stack direction="column" gap={0.5}>
                            <Typography variant="subtitle2">Condition details</Typography>
                            <Typography variant="caption">
                              Voucher-wide validation checks voucher existence, brand, stacking rules, promo-stacking
                              rules, account country/currency guards, used state, voucher-user ownership, currency,
                              reward amount, and remaining balance.
                            </Typography>
                            <Typography variant="caption">
                              Voucher conditions can filter specific reward rows when their Reward filter is set. If all
                              rewards are filtered out, or a condition is configured to disallow the whole voucher, the
                              voucher cannot be used.
                            </Typography>
                            <Typography variant="caption">
                              Product, min spend, destination, occupancy, booking date, margin, provider, fare, flight,
                              place id, subscriber tier, loyalty offer type, friends-and-family, and spoofing conditions
                              can remove matching rewards or block the voucher, depending on their configuration.
                            </Typography>
                            <Typography variant="caption">
                              Provide-credit rewards are calculated during /discount and rechecked during /vouchers/log
                              for credit amount, payout date, item end dates, and currency before the voucher log is
                              persisted.
                            </Typography>
                          </Stack>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              {!isPromoMetaLoading && promoMeta && promoMeta.voucher_toggles.length > 0 && (
                <Box mt={3}>
                  <Controller
                    name="voucherToggles"
                    control={control}
                    render={({ field }) => (
                      <SummaryToggleAccordion
                        title="Voucher Toggles"
                        options={voucherToggleOptionsWithUnknown}
                        selectedKeys={field.value ?? []}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={usersExpanded}
          onChange={(_, expanded) => {
            setUsersExpanded(expanded);
            if (expanded && isEdit && voucherId) {
              setShowExtendedWarnings(true);
              setVoucherWarningsExpanded(true);
            }
          }}
          slotProps={{ transition: { onEntered: () => bulkExpiryExpanded && scrollToBulkExpiryForm() } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography component="span" fontWeight={500}>
                Users
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                Who can use this voucher, and when it expires for them.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <VoucherUsers
                bulkExpiryExpanded={bulkExpiryExpanded}
                onBulkExpiryExpandedChange={setBulkExpiryExpanded}
                bulkExpiryFormRef={bulkExpiryFormRef}
                onBulkExpiryEntered={scrollToBulkExpiryForm}
                userIds={voucherUsers}
                onChange={setVoucherUsers}
                expiredAt={expiredAt}
                onExpiredAtChange={setExpiredAt}
                voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
                voucherUserExpiresInDays={watchedVoucherUserExpiresInDays ?? ''}
                showAdvanceDescription={showAdvanceDescription}
                disabled={isSubmitting}
                voucherId={voucherId}
                voucherName={voucherNameValue ?? ''}
                promoRequestsUserFilter={promoRequestsUserFilter}
                onPromoRequestsUserFilterChange={setPromoRequestsUserFilter}
              />
              <Accordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="voucher-lab-audience-sync-content"
                  id="voucher-lab-audience-sync-header"
                >
                  <Typography variant="subtitle2">Lab audience sync</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Controller
                    name="labAudienceId"
                    control={control}
                    render={({ field }) => {
                      const selected =
                        field.value == null
                          ? null
                          : (labAudienceOptions.find((option) => option.id === field.value) ?? {
                              id: field.value,
                              name: `Audience #${field.value} (unavailable)`,
                            });
                      return (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                          <Autocomplete
                            options={labAudienceOptions}
                            value={selected}
                            onChange={(_, next) => field.onChange(next?.id ?? null)}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'input') {
                                setLabAudienceSearch(value);
                              }
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            filterOptions={(options) => options}
                            loading={isLabAudiencesFetching}
                            disabled={isSubmitting}
                            fullWidth
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Lab audience"
                                helperText="Not saved on the voucher — used only for Sync to Lab"
                              />
                            )}
                          />
                          {isEdit && (
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={
                                field.value == null || syncLabAudienceMutation.isPending || isSubmitting || !voucherId
                              }
                              onClick={() => {
                                if (voucherId && field.value != null) {
                                  syncLabAudienceMutation.mutate({ id: voucherId, labAudienceId: field.value });
                                }
                              }}
                              sx={{ mt: { sm: 1 }, whiteSpace: 'nowrap' }}
                            >
                              {syncLabAudienceMutation.isPending ? 'Syncing…' : 'Sync to Lab'}
                            </Button>
                          )}
                        </Stack>
                      );
                    }}
                  />
                </AccordionDetails>
              </Accordion>
              {isEdit && voucherNameValue && brand && (
                <VoucherRecentRequests
                  voucherName={voucherNameValue}
                  userId={promoRequestsUserFilter}
                  brand={brand}
                  promoMeta={promoMeta}
                  defaultVisibleItemFields={defaultVisibleRequestItemFields}
                  showVoucherLogsAccordion={false}
                  displayMode="voucher"
                  focusRecentRequests={focusedVoucherId === voucherId}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded={focusedVoucherId === voucherId}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography component="span" fontWeight={500}>
                Activity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                Review recent promo requests, related items, and voucher logs.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {isEdit && voucherNameValue && brand && (
              <VoucherRecentRequests
                voucherName={voucherNameValue}
                userId={promoRequestsUserFilter}
                brand={brand}
                promoMeta={promoMeta}
                defaultVisibleItemFields={defaultVisibleRequestItemFields}
                focusRecentRequests={focusedVoucherId === voucherId}
              />
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography component="span" fontWeight={500}>
                Debug &amp; Delete
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={accordionSubtitleSx}>
                Diagnostics and irreversible voucher actions.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Debug
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {isEdit && <DebugModal type="generic" title="Voucher API Response" data={voucherResponse?.result} />}
                  <DebugModal type="generic" title="Promo Meta" data={promoMeta} />
                </Stack>
                {isEdit && voucherNameValue?.trim() && (
                  <Box sx={{ mt: 2 }}>
                    <VoucherCheck voucherName={voucherNameValue.trim()} />
                  </Box>
                )}
                {isEdit && voucherNameValue?.trim() && (
                  <Box sx={{ mt: 2 }}>
                    <PromoByToggleTester voucherName={voucherNameValue.trim()} />
                  </Box>
                )}
              </Box>
              {isEdit && voucherId ? (
                <Box>
                  <Typography variant="subtitle2" color="error.main" gutterBottom>
                    Danger Zone
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Deleting this voucher cannot be undone.
                    </Typography>
                    <Box>
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={deleteVoucherMutation.isPending || isSubmitting}
                      >
                        Delete voucher
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <StickyFormActions>
          <Button variant="contained" onClick={handleSaveVoucher} disabled={isSubmitting}>
            {isSubmitting ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save changes' : 'Create voucher'}
          </Button>
        </StickyFormActions>
      </Stack>
    </Container>
  );
}
